import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  message,
  Spin,
  Space,
  Typography,
  Upload,
  Statistic,
  Empty,
  Form,
  Radio,
  DatePicker,
  Modal,
  message as antdMessage,
  Input,
  Select,
  Drawer,
  Tooltip,
  Tag,
  Checkbox,
  Image
} from 'antd'
import {
  EditOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  ShoppingOutlined,
  FilterOutlined,
  FileTextOutlined,
  RightOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  QuestionCircleOutlined,
  WalletOutlined,
  CreditCardOutlined,
  IdcardOutlined,
  StarOutlined,
  GiftOutlined,
  MergeCellsOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { PHOTO_URL } from '../utils/apiutils'
import { postTopupWithDuplicateConfirm } from '../utils/duplicateTopup'
import {
  getCustomerDisplayData,
  getLineDisplayNameSuffix
} from '../utils/customerDisplay'
import { getCustomerAvatarSrc } from '../utils/customerAvatar'
import CustomerAvatar from '../components/common/CustomerAvatar'
import dayjs from 'dayjs'
import BookingTable from '../components/BookingTable'
import CustomerMergeModal from '../components/CustomerMergeModal'
import TopupSchemeKeyInput from '../components/TopupSchemeKeyInput'
import PillTabs from '../components/PillTabs'
import {
  listClassTicketsByCustomer,
  listClassTicketUsagesByCustomer
} from '../api/classTicket'
import PurchaseConsentModal from '../components/PurchaseConsentModal'
import ServicePhotoThumbs from '../components/ServicePhotoThumbs'
import CustomerServicePhotoRecords from '../components/CustomerServicePhotoRecords'
import ConsentAnswerList from '../components/ConsentAnswerList'
import {
  listConsentFormsByCustomer,
  getConsentFormDetail,
  consentFormPrintableUrl
} from '../api/consentForm'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { getCustomerInstallmentPlans } from '../api/installment'
import CustomerInstallmentsPanel from '../components/installment/CustomerInstallmentsPanel'
import CustomerPointsPanel from '../components/CustomerPointsPanel'
import Hint from '../components/common/Hint'
import { useNavigate, useParams } from 'react-router-dom'
import useGoBack from '../hooks/useGoBack'
import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import { formatTz, fromPicker, now as tzNow } from '../utils/timezone'

const { Text, Title } = Typography

// 客戶詳情頁：扁平介面（細邊界、無陰影）；整頁灰底由 App.js ContentWrapper 提供 #f5f5f5
const CD_SURFACE = '#ffffff'
const CD_BORDER = '#e6e8ec'
const CD_BORDER_SUBTLE = '#eceef2'

// 樣式組件
const Container = styled.div`
  padding: 12px 16px 24px;
  width: 100%;
  max-width: none;
  margin: 0;
  background: transparent;
  box-sizing: border-box;

  @media (min-width: 769px) {
    padding: 16px 20px 28px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: ${CD_SURFACE};
  border-radius: 10px;
  border: 1px solid ${CD_BORDER};
  box-shadow: none;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    padding: 12px;
    gap: 10px;
    row-gap: 12px;
  }
`

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  border: none;
  gap: 8px;
  flex-shrink: 0;
`

// 手機版時推到第二行（order: 3 + flex-basis: 100%），與 BackButton/Actions 同列就會擠壞電話號碼
const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 640px) {
    order: 3;
    flex-basis: 100%;
    width: 100%;
    padding-top: 4px;
    border-top: 1px solid ${CD_BORDER_SUBTLE};
  }
`

// header 右側操作按鈕容器
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  flex-shrink: 0;

  @media (max-width: 640px) {
    margin-left: auto;
  }
`

const CustomerDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const CustomerName = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #1c1c1e;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    font-size: 17px;
  }
`

const CustomerPhone = styled.div`
  color: #6b7280;
  font-size: 15px;
  margin-top: 2px;
  /* 關鍵：電話號碼絕對不換行；空間不夠就溢出省略，避免被折成兩行 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;

  @media (max-width: 640px) {
    font-size: 13.5px;
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  & > * {
    min-width: 0;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const InfoCard = styled.div`
  background: ${CD_SURFACE};
  border-radius: 10px;
  padding: 24px;
  border: 1px solid ${CD_BORDER};
  box-shadow: none;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #1c1c1e;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  row-gap: 6px;
  min-width: 0;
`

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
`

const InfoLabel = styled.span`
  font-weight: bold;
  flex-shrink: 0;
  width: 150px;
  color: #666;

  @media (max-width: 480px) {
    width: auto;
  }
`

const InfoValue = styled.span`
  flex: 1;
  min-width: 0;
  color: #333;
  white-space: wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  @media (max-width: 520px) {
    font-size: 14px;
  }
  @media (max-width: 320px) {
    font-size: 12px;
  }
`

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const TagBadge = styled.span`
  background-color: #f3f4f6;
  color: #3a3a3c;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid ${CD_BORDER_SUBTLE};
`

/** 顧客詳情：平台／LINE 狀態用莫蘭迪質感標籤 */
const MorandiStatusTagBase = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.35;
  border: 1px solid transparent;
`

const MorandiPlatformLinkedTag = styled(MorandiStatusTagBase)`
  color: #3a3d48;
  background: linear-gradient(
    152deg,
    #d4d3dc 0%,
    #c5c4cf 42%,
    #b8b6c4 100%
  );
  border-color: rgba(74, 78, 92, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    0 1px 3px rgba(58, 61, 72, 0.05);
`

const MorandiLinePairedTag = styled(MorandiStatusTagBase)`
  color: #2d3d35;
  background: linear-gradient(
    152deg,
    #c5d4c8 0%,
    #b3c7b8 45%,
    #a3bdaa 100%
  );
  border-color: rgba(45, 61, 53, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    0 1px 3px rgba(45, 61, 53, 0.06);
`

const MorandiStatusMutedTag = styled(MorandiStatusTagBase)`
  color: #6b6560;
  background: linear-gradient(
    152deg,
    #ebe8e4 0%,
    #e0dcd6 50%,
    #d5d0c9 100%
  );
  border-color: rgba(90, 84, 78, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
`

const BalanceCard = styled.div`
  background: linear-gradient(160deg, #d5728a 0%, #c45f77 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  border: 1px solid rgba(180, 72, 95, 0.35);
  box-shadow: none;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`

const BalanceAmount = styled.div`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
  text-shadow: none;
  word-break: break-all;
  overflow-wrap: anywhere;

  @media (max-width: 480px) {
    font-size: 26px;
  }
`

const BalanceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
`

const BalanceActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`

const BalanceButton = styled(Button)`
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  box-shadow: none !important;

  &.ant-btn-primary {
    background-color: #d5728a;
    border-color: #d5728a;
    &:hover {
      background-color: #c45f77;
      border-color: #c45f77;
    }
  }

  &:not(.ant-btn-primary) {
    border-color: #d5728a;
    color: #d5728a;
    background: rgba(255, 255, 255, 0.95);
    &:hover {
      border-color: #c45f77;
      color: #c45f77;
      background: #fff;
    }
  }
`

const SchemeBalanceSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
`

const SchemeBalanceTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  opacity: 0.95;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
`

const SchemeBalanceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SchemeBalanceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.28);
`

const SchemeBalanceName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
`

const SchemeBalanceAmount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
`

const SchemeBalanceExpiry = styled.span`
  font-size: 11px;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.9);
  width: 100%;
  margin-top: 2px;
  text-align: left;
`

const TransactionList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid ${CD_BORDER};
  border-radius: 10px;
  background: ${CD_SURFACE};
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
`

// 堂票卡片列表
const ClassTicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

/**
 * 預約記錄列表。
 *
 * 桌機分兩欄（一欄五筆、共十筆），一次看得到的資料量加倍；手機維持單欄，
 * 兩欄在窄螢幕上每格只剩一半寬度，服務名稱會被擠成兩三行反而更難讀。
 */
/* 預覽區顯示幾筆：桌機兩欄 × 五筆。改這個常數，
   標題的「查看全部」與底部的「查看更多」門檻會一起跟著變 */
const BOOKING_PREVIEW_COUNT = 10

/**
 * 客戶資產分頁的順序，第一個同時也是「沒有任何紀錄時」的預設值。
 * 堂票排在儲值金前面：實際在用這一區的品牌方案店家裡，堂票的資料量遠大於
 * 儲值（例如 W美胸 485 張堂票、0 位客人有餘額）。
 */
// installment（分期）只有高階方案＋有分期權限才會出現，見 installmentTabEnabled
// points（紅利點數）只有商務／品牌方案＋有點數權限才會出現，見 pointsTabEnabled
const ASSET_TAB_KEYS = [
  'classTicket',
  'storedValue',
  'points',
  'installment',
  'frequentItems'
]

/* 記住這台裝置上次看的是哪一頁。同步讀取，畫面不會先閃一下再跳 */
const CUSTOMER_ASSET_TAB_KEY = 'beauty:customerAssetTab'

const BookingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
`

const BookingRow = styled.div`
  padding: 9px 10px;
  border-bottom: 1px solid #f2f2f4;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.18s ease;
  min-width: 0;

  &:hover {
    background-color: #f8f9fa;
  }
`

const BookingTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const BookingName = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 13.5px;
  color: #2c2c2c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BookingTags = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
`

const BookingTag = styled.span`
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) => props.$bg};
  color: #fff;
`

const BookingMetaLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 3px;
  font-size: 12px;
  color: #8c96a5;
  min-width: 0;

  .meta-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta-amount {
    flex: none;
    font-weight: 600;
    color: #d5728a;
  }
`

/* 備註只給一行，超出用 ... 收掉；完整內容點進去看 */
const BookingNote = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: #9aa0a6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* 這一行可能是結帳備註也可能是預約備註，兩者的意義差很多
     （做完當下寫的 vs 客人訂的時候留的），所以標出來源 */
  .note-label {
    display: inline-block;
    margin-right: 6px;
    padding: 0 5px;
    border-radius: 4px;
    background: #f1f3f4;
    color: #80868b;
    font-size: 11px;
    line-height: 16px;
    vertical-align: 1px;
  }

  /* 結帳備註是店家回頭翻紀錄時真正要看的那一份，用店內的金色標出來 */
  .note-label.checkout {
    background: #fdf3e3;
    color: #8a6430;
  }
`

/**
 * 堂票使用歷程。
 *
 * 版型刻意跟預約紀錄一樣：左邊一條時間軸、一列一筆、由新到舊。
 * 客人有兩三張票時，原本要一張一張點進去看使用紀錄，來回切很麻煩；
 * 這裡把所有票的歷程混在同一條時間線上，直接看得出「他上次是什麼時候
 * 用掉哪一張的哪一堂」。
 */
const UsageTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 4px;
`

const UsageRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #f4f4f5;

  &:last-child {
    border-bottom: none;
  }
`

const UsageDot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  background: ${(props) => props.$color || '#d5728a'};
`

const UsageBody = styled.div`
  flex: 1;
  min-width: 0;
`

const UsageTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #2c2c2c;

  b {
    font-weight: 600;
  }
`

const UsageMeta = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #8c96a5;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const ClassTicketCard = styled.div`
  border: 1px solid ${CD_BORDER};
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #d5728a;
    box-shadow: 0 4px 12px rgba(213, 114, 138, 0.08);
  }
`

const ClassTicketTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const ClassTicketName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1c1c1e;
  flex: 1;
  min-width: 0;
`

const ClassTicketCode = styled.div`
  font-size: 11px;
  color: #8e8e93;
  margin-top: 2px;
  font-family: ui-monospace, SFMono-Regular, monospace;
`

const ClassTicketStatusTag = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => p.$bg || '#eef0f3'};
  color: ${(p) => p.$color || '#7f8c8d'};
`

const ClassTicketProgressWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`

const ClassTicketProgressBg = styled.div`
  flex: 1;
  height: 5px;
  background: #f0f1f4;
  border-radius: 3px;
  overflow: hidden;
`

const ClassTicketProgressFill = styled.div`
  height: 100%;
  background: ${(p) => p.$color || '#d5728a'};
  width: ${(p) => p.$ratio * 100}%;
`

const ClassTicketProgressText = styled.div`
  font-size: 12px;
  color: #6b7280;

  b {
    color: #d5728a;
    font-weight: 700;
  }
`

const ClassTicketFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 11px;
  color: #8e8e93;
`

// 常做項目
const FrequentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FrequentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${CD_BORDER_SUBTLE};

  &:last-child {
    border-bottom: none;
  }
`

const FrequentRank = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 13px;
  background: #fdf0f4;
  color: #d5728a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  margin-right: 12px;
  flex-shrink: 0;
`

const FrequentName = styled.div`
  flex: 1;
  font-size: 14px;
  color: #1c1c1e;
`

const FrequentCount = styled.div`
  font-size: 13px;
  color: #d5728a;
  font-weight: 700;
`

const TabbedAssetWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TransactionItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${CD_BORDER_SUBTLE};
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`

const TransactionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  min-width: 0;

  > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`

const TransactionType = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.type) {
      case 'topup':
        return '#f6ffed'
      case 'deduct':
        return '#fff2e8'
      case 'refund':
        return '#f6ffed'
      case 'adjustment':
        return '#f0f0f0'
      default:
        return '#f0f0f0'
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case 'topup':
        return '#52c41a'
      case 'deduct':
        return '#fa8c16'
      case 'refund':
        return '#52c41a'
      case 'adjustment':
        return '#666'
      default:
        return '#666'
    }
  }};
`

const TransactionAmount = styled.span`
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
  text-align: right;
  white-space: nowrap;
  color: ${(props) => {
    if (props.amount > 0) return '#52c41a'
    if (props.amount < 0) return '#ff4d4f'
    return '#666'
  }};

  @media (max-width: 480px) {
    width: 100%;
    text-align: left;
    white-space: normal;
  }
`

const TransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
`

const TransactionNote = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`

const TransactionTime = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`

const TransactionOperator = styled.div`
  font-size: 12px;
  color: #595959;
`

const TransactionMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

// 統計信息樣式組件
const StatsSection = styled.div`
  margin: 24px 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (min-width: 768px) and (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
`

const StatItem = styled.div`
  background: ${CD_SURFACE};
  padding: 16px;
  border-radius: 10px;
  border: 1px solid ${CD_BORDER};
  box-shadow: none;
  text-align: center;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #d8dce3;
    background: #fafbfc;
  }

  .ant-statistic {
    .ant-statistic-title {
      color: #8c8c8c;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .ant-statistic-content {
      font-size: 20px;
      color: #333;

      .ant-statistic-content-prefix {
        margin-right: 4px;
      }
    }
  }
`

// 照片管理樣式組件
const PhotoWall = styled.div`
  margin: 16px 0;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  /* 編輯模式 antd Upload 的 picture-card：把預設粗邊框拿掉，換成輕薄背景 */
  .ant-upload-list-picture-card {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 10px;
  }
  .ant-upload-list-picture-card-container,
  .ant-upload.ant-upload-select-picture-card {
    width: 100% !important;
    height: 96px !important;
    border: none !important;
    border-radius: 12px !important;
    background: #f4f5f7 !important;
    margin: 0 !important;
    overflow: hidden;
  }
  .ant-upload.ant-upload-select-picture-card:hover {
    background: #ecedf0 !important;
    border-color: #d5728a !important;
  }
  .ant-upload-list-item {
    border-radius: 12px !important;
    border: none !important;
    padding: 0 !important;
    overflow: hidden;
  }
  .ant-upload-list-item-thumbnail img {
    object-fit: cover !important;
  }
`

// 檢視模式照片網格（無邊框、純圖片、自動 RWD）
const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 10px;
  margin: 16px 0 4px;
`

const PhotoTile = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f4f5f7;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 37, 98, 0.12);
  }

  .ant-image,
  .ant-image-img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
  }
`

const PhotoOverflow = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(28, 28, 30, 0.55);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  font-weight: 600;

  .overflow-num {
    font-size: 22px;
    line-height: 1;
    margin-bottom: 4px;
  }
  .overflow-hint {
    font-size: 11px;
    opacity: 0.85;
  }
`

const PhotoSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`

const PhotoCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 10px;
  background: #fdf0f4;
  color: #d5728a;
  font-size: 11px;
  font-weight: 700;
  margin-left: 8px;
`

const PhotoMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  padding: 20px;
  z-index: 2;
`

const UpgradeText = styled.div`
  color: white;
  font-size: 16px;
  margin-bottom: 12px;
  text-shadow: none;
`

// 現代化儲值金Modal樣式
const ModernModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: none;
    border: 1px solid ${CD_BORDER};
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 16px 20px;
    flex-shrink: 0;

    .ant-modal-title {
      color: white;
      font-size: 16px;
      font-weight: 600;
      text-align: center;
    }

    .ant-modal-close {
      color: white !important;
      top: 16px;
      right: 20px;
      z-index: 10;

      &:hover {
        color: rgba(255, 255, 255, 0.8) !important;
      }
    }
  }

  .ant-modal-body {
    padding: 0;
    background: white;
    flex: 1;
    overflow-y: auto;
  }

  .ant-modal-footer {
    border: none;
    padding: 16px 20px;
    background: white;
    border-radius: 0 0 24px 24px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .ant-modal-content {
      border-radius: 0;
      border: none;
      height: 100vh;
      max-height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .ant-modal-header {
      padding: 12px 16px;
      flex-shrink: 0;
    }

    .ant-modal-body {
      flex: 1;
      overflow-y: auto;
    }

    .ant-modal-footer {
      padding: 12px 16px;
      flex-shrink: 0;
    }

    .ant-modal-close {
      top: 12px !important;
      right: 16px !important;
    }
  }
`

const ModalContent = styled.div`
  padding: 20px;
  background: white;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
    flex: 1;
    min-height: 0;
  }
`

const AmountDisplay = styled.div`
  text-align: center;
  margin-bottom: 16px;

  .amount-label {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 6px;
    font-weight: 500;
  }

  .amount-value {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
    font-family:
      'SF Pro Display',
      -apple-system,
      BlinkMacSystemFont,
      sans-serif;
  }

  .currency {
    font-size: 16px;
    color: #64748b;
    margin-left: 6px;
  }

  @media (max-width: 768px) {
    margin-bottom: 14px;

    .amount-value {
      font-size: 24px;
    }

    .currency {
      font-size: 14px;
    }
  }
`

const KeypadContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`

const KeypadButton = styled.button`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  height: 42px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:active {
    background: #e2e8f0;
  }

  &.clear {
    background: linear-gradient(135deg, #fef2f2, #fee2e2);
    border-color: #fecaca;
    color: #dc2626;

    &:hover {
      background: linear-gradient(135deg, #fee2e2, #fecaca);
      border-color: #fca5a5;
    }
  }

  &.backspace {
    background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
    border-color: #bae6fd;
    color: #0284c7;

    &:hover {
      background: linear-gradient(135deg, #e0f2fe, #bae6fd);
      border-color: #7dd3fc;
    }
  }

  @media (max-width: 768px) {
    height: 48px;
    font-size: 20px;
  }
`

const OperationButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
`

const OperationButton = styled.button`
  background: ${(props) => {
    if (props.selected) {
      return props.type === 'increase'
        ? 'linear-gradient(135deg, #10b981, #059669)'
        : 'linear-gradient(135deg, #f59e0b, #d97706)'
    }
    return 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
  }};
  border: ${(props) => {
    if (props.selected) {
      return 'none'
    }
    return '2px solid #cbd5e1'
  }};
  border-radius: 10px;
  height: 40px;
  color: ${(props) => (props.selected ? 'white' : '#64748b')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  position: relative;

  &:hover {
    filter: brightness(0.98);
  }

  &:active {
    filter: brightness(0.96);
  }

  &.selected {
    outline: 2px solid
      ${(props) =>
        props.type === 'increase'
          ? 'rgba(16, 185, 129, 0.55)'
          : 'rgba(245, 158, 11, 0.55)'};
    outline-offset: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) => (props.selected ? 'white' : 'transparent')};
    display: ${(props) => (props.selected ? 'flex' : 'none')};
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  &::after {
    content: '✓';
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    color: ${(props) => (props.type === 'increase' ? '#10b981' : '#f59e0b')};
    display: ${(props) => (props.selected ? 'flex' : 'none')};
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }
`

const NoteSection = styled.div`
  margin-bottom: 16px;

  .note-label {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 6px;
    font-weight: 500;
  }

  .note-input {
    width: 100%;
    padding: 10px 12px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
    resize: none;
    transition: all 0.2s ease;
    box-sizing: border-box;
    font-family: inherit;
    min-height: 60px;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid rgba(102, 126, 234, 0.35);
      outline-offset: 1px;
    }
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;

  .cancel-btn {
    flex: 1;
    height: 40px;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
  }

  .confirm-btn {
    flex: 2;
    height: 40px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border: none;
    border-radius: 10px;
    color: white;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      filter: brightness(1.05);
    }

    &:active {
      filter: brightness(0.98);
    }
  }

  @media (max-width: 768px) {
    margin-top: 0;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
`

const WarningBox = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 1px solid #f59e0b;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 16px;

  .warning-text {
    color: #92400e;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    line-height: 1.4;
  }
`

// 會員等級顏色映射
const membershipColors = {
  普通會員: 'linear-gradient(135deg, #ca9f71, #9c5e1c)',
  銀卡會員: 'linear-gradient(135deg, #e0e0e0, #c0c0c0)',
  金卡會員: 'linear-gradient(135deg, #ffd700, #ffaa00)',
  鑽石會員: 'linear-gradient(135deg, #b9f2ff, #00bfff)',
  VIP會員: 'linear-gradient(135deg, #ff69b4, #ff1493)'
}

// 會員等級顯示組件
const MembershipBadge = styled.span`
  background: ${(props) => props.color || '#1890ff'};
  color: ${(props) =>
    props.color && props.color.includes('#ffd700') ? '#000' : '#fff'};
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: bold;
  box-shadow: none;
  text-shadow: none;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

// 會員等級轉換函數
const getMembershipDisplay = (level) => {
  const chineseLevel =
    {
      regular: '普通會員',
      silver: '銀卡會員',
      gold: '金卡會員',
      diamond: '鑽石會員',
      vip: 'VIP會員'
    }[level?.toLowerCase()] || '普通會員'

  return {
    text: chineseLevel,
    color: membershipColors[chineseLevel]
  }
}

const CustomerDetail = () => {
  const { customerId } = useParams()
  const { hasFeature: hasPlanFeature, permissionsLoaded } = useMembership()
  const { canAny } = usePermission()
  const consentEnabled = hasPlanFeature('consentForm')
  /**
   * 分期：客人下次來店時，接待的人要一眼看得到「這位還有分期沒繳完」。
   * 原本只能去分期收款頁搜尋，客人一多、換人接待就找不到。
   */
  const installmentTabEnabled =
    hasPlanFeature('installment') &&
    canAny(['installment:view', 'installment:manage'])
  const pointsTabEnabled =
    hasPlanFeature('points') &&
    canAny(['points:view', 'points:manage', 'points:settings'])
  // 點數面板異動後的最新餘額；null = 還沒動過，沿用顧客資料上的數字
  const [pointsBalance, setPointsBalance] = useState(null)
  const [installmentSummary, setInstallmentSummary] = useState(null)
  /**
   * 最近一次的問卷作答。
   *
   * 術前問卷（用藥、手術史）在施作前最需要被看到，但它躺在同意書紀錄裡，
   * 要先想起客人簽過哪一份才翻得到。這裡直接把最新一份帶有問卷的答案
   * 攤在客戶頁上，省掉那一趟。
   */
  const [latestConsentAnswers, setLatestConsentAnswers] = useState(null)
  const navigate = useNavigate()
  const goBack = useGoBack('/customers')
  const { businessId } = useAuth()

  // 客戶信息狀態
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)

  // 儲值金相關狀態
  const [balanceInfo, setBalanceInfo] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [transactionHistory, setTransactionHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // 預約記錄狀態
  const [customerBookings, setCustomerBookings] = useState(null)
  const [bookingsLoading, setBookingsLoading] = useState(false)

  // 客戶資產 Tab（儲值金 / 堂票 / 常做項目）
  /**
   * 客戶資產分頁的預設值。
   *
   * 先前試過「等餘額與堂票都載完再自動挑一個」，但兩支 API 回來之前畫面
   * 已經先渲染成儲值金，資料到了才跳到堂票 —— 每次進客戶頁都閃一下，
   * 比手動點一次還煩。任何「等資料再決定」的做法都會有這個問題。
   *
   * 改成同步決定，畫面從第一幀就是對的：
   *   1. 分頁順序把堂票排到第一
   *   2. 預設選中上次店家用過的那一個（存在 localStorage，讀取是同步的）
   *
   * 不寫死特定店家 ID —— 那只解決一家店，而且是看不見的技術債；
   * 記住「上次用哪個」對每一家都成立：常用堂票的店按一次之後就固定開堂票，
   * 常用儲值的店按一次之後就固定開儲值金。
   */
  const [assetTab, setAssetTab] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMER_ASSET_TAB_KEY)
      if (saved && ASSET_TAB_KEYS.includes(saved)) return saved
    } catch (e) {
      // 隱私模式或封鎖 storage 時直接用預設值，不要讓整頁掛掉
    }
    return ASSET_TAB_KEYS[0]
  })

  // 上次停在「分期」但這個帳號沒有分期功能／權限 → 退回預設分頁
  useEffect(() => {
    if (permissionsLoaded && assetTab === 'installment' && !installmentTabEnabled) {
      setAssetTab(ASSET_TAB_KEYS[0])
    }
    if (permissionsLoaded && assetTab === 'points' && !pointsTabEnabled) {
      setAssetTab(ASSET_TAB_KEYS[0])
    }
  }, [permissionsLoaded, assetTab, installmentTabEnabled, pointsTabEnabled])

  // 分期摘要（分頁上的數字＋頂端提示用）；收款後由面板回呼更新
  const fetchInstallmentSummary = useCallback(async () => {
    if (!installmentTabEnabled || !businessId || !customerId) {
      setInstallmentSummary(null)
      return
    }
    try {
      const res = await getCustomerInstallmentPlans(businessId, customerId)
      setInstallmentSummary(res?.data?.data?.summary || null)
    } catch (e) {
      setInstallmentSummary(null)
    }
  }, [installmentTabEnabled, businessId, customerId])

  useEffect(() => {
    fetchInstallmentSummary()
  }, [fetchInstallmentSummary])

  // 堂票
  const [classTickets, setClassTickets] = useState([])
  const [classTicketsLoading, setClassTicketsLoading] = useState(false)
  // 堂票使用歷程（跨這位客人所有的票，依時間由新到舊）
  const [classTicketUsages, setClassTicketUsages] = useState([])

  // 儲值金操作Modal狀態
  const [balanceModalVisible, setBalanceModalVisible] = useState(false)
  const [balanceConsent, setBalanceConsent] = useState({
    open: false,
    customerId: null,
    customerName: '',
    variables: {}
  })
  const [balanceOperation, setBalanceOperation] = useState('')
  const [balanceForm] = Form.useForm()
  const [adjustmentType, setAdjustmentType] = useState('increase')

  // 現代化Modal狀態
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [balanceSchemeKey, setBalanceSchemeKey] = useState('') // 儲值活動（僅儲值時使用）
  const [balanceExpiryDate, setBalanceExpiryDate] = useState(null) // 使用期限（僅儲值時使用）
  const [balanceSalesPersonnelId, setBalanceSalesPersonnelId] = useState(null) // 銷售人員（僅儲值時使用，有選才會進報表）
  const [balanceSelectedTopupBonus, setBalanceSelectedTopupBonus] = useState(null) // 選中的等級儲值回饋
  const [balanceBonusOptedOut, setBalanceBonusOptedOut] = useState(false) // 主動選擇不套用活動方案
  const [balanceAssignTierName, setBalanceAssignTierName] = useState(null) // 手動指派會員等級（選填）
  const [balanceSubmitting, setBalanceSubmitting] = useState(false) // 防止重複送出

  // 編輯狀態
  const [isEditing, setIsEditing] = useState(false)
  const [editedCustomer, setEditedCustomer] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [customerPhotos, setCustomerPhotos] = useState([])
  // 結帳時上傳的施作照片張數（由 CustomerServicePhotoRecords 回報），
  // 用來決定「暫無照片」要不要出現、以及照片記錄的總數徽章
  const [servicePhotoTotal, setServicePhotoTotal] = useState(0)
  const [tagOptions, setTagOptions] = useState([
    '遲到',
    '價格敏感',
    '過敏',
    '加強關懷'
  ])
  const [membershipTiers, setMembershipTiers] = useState([])
  const [membershipTiersLoading, setMembershipTiersLoading] = useState(false)

  // 會員等級調整 Modal
  const [tierAdjustOpen, setTierAdjustOpen] = useState(false)
  const [tierAdjustSubmitting, setTierAdjustSubmitting] = useState(false)
  const [tierAdjustForm] = Form.useForm()
  const [tierAdjustPermanent, setTierAdjustPermanent] = useState(false)

  // 結帳信息相關狀態
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false)
  const [checkoutOrderData, setCheckoutOrderData] = useState(null)
  const [loadingCheckout, setLoadingCheckout] = useState(false)
  const [employees, setEmployees] = useState([])

  // 預約記錄 Drawer 相關狀態
  const [bookingsDrawerVisible, setBookingsDrawerVisible] = useState(false)
  const [allCustomerBookings, setAllCustomerBookings] = useState(null)
  const [loadingAllBookings, setLoadingAllBookings] = useState(false)

  // 所屬群組
  const [customerGroups, setCustomerGroups] = useState([])
  const [customerGroupsLoading, setCustomerGroupsLoading] = useState(false)

  // 合併 Modal
  const [mergeModalOpen, setMergeModalOpen] = useState(false)

  // 獲取客戶詳情
  const fetchCustomerDetails = async () => {
    if (!customerId) return

    setLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/customers/${customerId}`
      )
      if (response.data.success) {
        const customerData = response.data.customer || response.data.data
        setCustomer(customerData)

        // 初始化編輯資料
        const customerCopy = JSON.parse(JSON.stringify(customerData))
        if (!customerCopy.businessSpecificData) {
          customerCopy.businessSpecificData = {}
        }
        const displayData = getCustomerDisplayData(customerCopy)
        customerCopy.businessSpecificData = {
          ...customerCopy.businessSpecificData,
          name: displayData.name !== '未知' ? displayData.name : '',
          phone: displayData.phone !== '未提供' ? displayData.phone : '',
          email: displayData.email !== '未知' ? displayData.email : '',
          gender: displayData.gender || '',
          birthdate: displayData.birthdate || '',
          region: displayData.region || '',
          notes: displayData.notes || '',
          tags: displayData.tags || [],
          membershipLevel:
            displayData.membershipLevel || displayData.membershipTier || '',
          points: displayData.points || 0,
          totalSpent: displayData.totalSpent || 0,
          blacklist: displayData.blacklist || false
        }
        setEditedCustomer(customerCopy)

        // 設置客戶照片
        setCustomerPhotos(
          (
            customerData.photos ||
            customerData.businessSpecificData?.photos ||
            []
          ).map((photo, index) => ({
            uid: photo._id || `photo-${index}`,
            name: `photo-${index}`,
            status: 'done',
            url: `${PHOTO_URL}${photo.url}`
          }))
        )
      }
    } catch (error) {
      console.error('獲取客戶詳情失敗:', error)
      message.error('獲取客戶詳情失敗')
    } finally {
      setLoading(false)
    }
  }

  // 獲取客戶儲值金餘額
  const fetchCustomerBalance = async () => {
    if (!customerId) return

    setBalanceLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/customers/${customerId}/balance`
      )
      if (response.data.success) {
        setBalanceInfo(response.data.data)
      }
    } catch (error) {
    } finally {
      setBalanceLoading(false)
    }
  }

  // 獲取顧客所屬群組
  const fetchCustomerGroups = async () => {
    if (!businessId || !customerId) return
    setCustomerGroupsLoading(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/${customerId}/groups`
      )
      const list = res.data?.data ?? res.data ?? []
      setCustomerGroups(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error('取得所屬群組失敗:', e)
      setCustomerGroups([])
    } finally {
      setCustomerGroupsLoading(false)
    }
  }

  // 獲取儲值金交易歷史
  const fetchTransactionHistory = async (page = 1) => {
    if (!customerId) return

    setHistoryLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/customers/${customerId}/balance/transactions`,
        {
          params: {
            page,
            limit: 10
          }
        }
      )
      if (response.data.success) {
        setTransactionHistory(response.data.data.transactions)
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false)
    }
  }

  // 獲取客戶預約記錄（後端 getCustomerBookings 會併查：customerId、配對之 userId、平台/店家電話與信箱，舊預約才會出現）
  const fetchCustomerBookings = async (page = 1) => {
    if (!customerId) return

    setBookingsLoading(true)
    try {
      const response = await api.get(
        `/api/business/bookings/customer/${customerId}`,
        {
          params: {
            page,
            limit: 50
          }
        }
      )
      console.log('customer bookings data:', response.data)
      setCustomerBookings(response.data)
    } catch (error) {
      console.error('獲取客戶預約失敗:', error)
    } finally {
      setBookingsLoading(false)
    }
  }

  // 獲取所有客戶預約記錄（用於 Drawer 顯示）
  const fetchAllCustomerBookings = async (page = 1) => {
    if (!customerId) return

    setLoadingAllBookings(true)
    try {
      const response = await api.get(
        `/api/business/bookings/customer/${customerId}`,
        {
          params: {
            page,
            limit: 50
          }
        }
      )
      setAllCustomerBookings(response.data)
    } catch (error) {
      console.error('獲取所有客戶預約失敗:', error)
      message.error('獲取預約記錄失敗')
    } finally {
      setLoadingAllBookings(false)
    }
  }

  // 處理查看所有預約記錄
  const handleViewAllBookings = () => {
    setBookingsDrawerVisible(true)
    if (!allCustomerBookings) {
      fetchAllCustomerBookings(1)
    }
  }

  // 儲值金操作
  const handleBalanceOperation = (operation) => {
    setBalanceOperation(operation)
    setBalanceModalVisible(true)
    balanceForm.resetFields()

    // 重置現代化Modal狀態
    setAmount('')
    setNote('')
    setBalanceSchemeKey('')
    setBalanceExpiryDate(null)
    setBalanceSalesPersonnelId(null)
    setBalanceSelectedTopupBonus(null)
    setBalanceBonusOptedOut(false)

    // 確保調整操作時默認選中增加餘額
    if (operation === 'adjust') {
      setAdjustmentType('increase')
    }
  }

  // 數字鍵盤處理函數
  const handleKeypadInput = (value) => {
    if (value === 'clear') {
      setAmount('')
    } else if (value === 'backspace') {
      setAmount((prev) => prev.slice(0, -1))
    } else {
      setAmount((prev) => prev + value)
    }
  }

  // 格式化金額顯示
  const formatAmount = (amount) => {
    if (!amount) return '0'
    return parseInt(amount).toLocaleString()
  }

  // 提交儲值金操作
  const handleBalanceSubmit = async (consentSignatureId = null, consentResolved = false) => {
    // 防呆：按鈕 onClick 會把事件當第一參數傳入
    const sigId = typeof consentSignatureId === 'string' ? consentSignatureId : null
    if (!customerId) return
    // ── 防重複送出：若正在處理中則直接 return ────────────────────────────────
    if (balanceSubmitting) return

    // 驗證金額
    if (!amount || parseInt(amount) <= 0) {
      antdMessage.error('請輸入有效金額')
      return
    }

    // 調整操作時驗證調整類型
    if (balanceOperation === 'adjust' && !adjustmentType) {
      antdMessage.error('請選擇增加或減少餘額')
      return
    }

    // 調整操作時驗證備註
    if (balanceOperation === 'adjust' && !note.trim()) {
      antdMessage.error('調整餘額必須填寫調整原因')
      return
    }

    // 儲值：先走購買同意書（無綁定則直接儲值；現場簽回傳 signatureId）
    if (balanceOperation === 'topup' && !consentResolved) {
      const displayName = getCustomerDisplayData?.(customer)?.name || ''
      const tierName =
        balanceSelectedTopupBonus?.tierName || balanceAssignTierName || ''
      setBalanceConsent({
        open: true,
        customerId,
        customerName: displayName,
        variables: {
          planName: (balanceSchemeKey && balanceSchemeKey.trim()) || tierName || '儲值',
          amount: parseInt(amount) || 0,
          price: parseInt(amount) || 0,
          expiryDate: balanceExpiryDate
            ? balanceExpiryDate.format('YYYY/MM/DD')
            : '',
          customerName: displayName
        }
      })
      return
    }

    setBalanceSubmitting(true)
    try {
      let endpoint = ''
      let requestData = {
        amount: parseInt(amount),
        note: note || ''
      }

      switch (balanceOperation) {
        case 'topup':
          endpoint = `/api/businesses/${businessId}/customers/${customerId}/balance/topup`
          if (balanceSchemeKey?.trim())
            requestData.schemeKey = balanceSchemeKey.trim()
          if (balanceExpiryDate)
            requestData.expiryDate = balanceExpiryDate.format('YYYY-MM-DD')
          if (balanceSalesPersonnelId)
            requestData.salesPersonnelId = balanceSalesPersonnelId
          if (balanceBonusOptedOut) {
            requestData.bonusOptedOut = true
          } else if (balanceSelectedTopupBonus) {
            requestData.bonusAmount = Number(balanceSelectedTopupBonus.bonus) || 0
            requestData.selectedTierName = balanceSelectedTopupBonus.tierName
          }
          if (balanceAssignTierName?.trim())
            requestData.assignTierName = balanceAssignTierName.trim()
          if (sigId) requestData.consentSignatureId = sigId
          break
        case 'deduct':
          endpoint = `/api/businesses/${businessId}/customers/${customerId}/balance/deduct`
          break
        case 'refund':
          endpoint = `/api/businesses/${businessId}/customers/${customerId}/balance/refund`
          break
        case 'adjust':
          endpoint = `/api/businesses/${businessId}/customers/${customerId}/balance/adjust`
          requestData.amount =
            adjustmentType === 'increase' ? parseInt(amount) : -parseInt(amount)
          break
        default:
          return
      }

      // 儲值遇到「可能重複儲值」時先請店家確認；取消就停在視窗讓他檢查
      const response =
        balanceOperation === 'topup'
          ? await postTopupWithDuplicateConfirm(api, endpoint, requestData)
          : await api.post(endpoint, requestData)
      if (!response) return

      if (response.data.success) {
        antdMessage.success(response.data.message || '操作成功')
        setBalanceModalVisible(false)
        setAmount('')
        setNote('')
        setBalanceSchemeKey('')
        setBalanceExpiryDate(null)
        setBalanceSalesPersonnelId(null)
        setBalanceSelectedTopupBonus(null)
        setBalanceBonusOptedOut(false)
        setBalanceAssignTierName(null)
        // 重新獲取餘額、交易歷史、客戶詳情（含會員等級）
        fetchCustomerBalance()
        fetchTransactionHistory()
        fetchCustomerDetails()
      } else {
        antdMessage.error(response.data.error || '操作失敗')
      }
    } catch (error) {
      console.error('儲值金操作失敗:', error)
      antdMessage.error(error.response?.data?.error || '操作失敗')
    } finally {
      setBalanceSubmitting(false)
    }
  }

  // 編輯功能處理函數
  const handleEdit = () => {
    setIsEditing(true)
    // 確保 businessSpecificData 存在
    const customerCopy = JSON.parse(JSON.stringify(customer))
    if (!customerCopy.businessSpecificData) {
      customerCopy.businessSpecificData = {}
    }
    // 使用顯示資料作為初始值
    const displayData = getCustomerDisplayData(customerCopy)
    customerCopy.businessSpecificData = {
      ...customerCopy.businessSpecificData,
      name: displayData.name !== '未知' ? displayData.name : '',
      phone: displayData.phone !== '未提供' ? displayData.phone : '',
      email: displayData.email !== '未知' ? displayData.email : '',
      gender: displayData.gender || '',
      birthdate: displayData.birthdate || '',
      region: displayData.region || '',
      notes: displayData.notes || '',
      tags: displayData.tags || [],
      membershipTier: displayData.membershipTier || '',
      points: displayData.points || 0,
      totalSpent: displayData.totalSpent || 0,
      blacklist: displayData.blacklist || false
    }
    setEditedCustomer(customerCopy)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedCustomer(null)
  }

  const handleDelete = async () => {
    Modal.confirm({
      title: '確認刪除客戶',
      content: (
        <div>
          <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            確定要刪除此客戶嗎？
          </p>
          <p style={{ color: '#ff4d4f', fontSize: '13px' }}>
            ⚠️ 此操作不可復原，客戶將從列表中移除。
          </p>
        </div>
      ),
      okText: '確認刪除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      width: 400,
      onOk: async () => {
        try {
          const response = await api.delete(
            `/api/business_B/${businessId}/customers/${customerId}`
          )
          if (response.data.success) {
            message.success('客戶已成功刪除')
            // 返回上一頁（多半是客戶列表，保留原本的頁數與搜尋狀態）
            goBack()
          } else {
            message.error(response.data.error || '刪除失敗')
          }
        } catch (error) {
          console.error('刪除客戶失敗:', error)
          const errorMessage =
            error.response?.data?.error || '刪除客戶失敗，請稍後再試'
          message.error(errorMessage)
        }
      }
    })
  }

  const handleSave = async () => {
    try {
      setIsUploading(true)

      // 驗證姓名是否填寫
      const businessData = editedCustomer.businessSpecificData || {}
      if (!businessData.name?.trim()) {
        message.error('請輸入姓名')
        setIsUploading(false)
        return
      }

      // 構建請求數據（所有資料都保存到 businessSpecificData）
      const requestData = {
        action: 'manualUpdate',
        name: businessData.name?.trim() || '',
        phone: businessData.phone || '',
        email: businessData.email || '',
        gender: businessData.gender || '',
        birthdate: businessData.birthdate || '',
        region: businessData.region || '',
        tags: businessData.tags || [],
        notes: businessData.notes || '',
        membershipLevel:
          businessData.membershipLevel || businessData.membershipTier || '',
        // 點數不從編輯表單送：後端已不接受，一律走紅利點數面板（有異動紀錄）
        totalSpent: businessData.totalSpent || 0,
        blacklist: businessData.blacklist || false
      }

      // 處理照片相關邏輯
      const originalPhotos =
        customer.photos || customer.businessSpecificData?.photos || []
      const originalPhotoIds =
        originalPhotos.map((p) => p._id).filter(Boolean) || []
      // 保留中的項目：含遠端 url、本機預覽 thumbUrl、或尚未上傳的 originFileObj（避免誤判已刪）
      const currentPhotoIds = (customerPhotos || [])
        .filter((p) => p.url || p.thumbUrl || p.originFileObj)
        .map((p) => p.uid)
      const deletedPhotoIds = (originalPhotoIds || []).filter(
        (id) => id && currentPhotoIds && !currentPhotoIds.includes(id)
      )

      const hasNewPhotos = customerPhotos.some((p) => p.originFileObj)
      const hasDeletedPhotos = deletedPhotoIds.length > 0
      const hasPhotos = hasNewPhotos || hasDeletedPhotos

      let response

      if (hasPhotos) {
        // 有照片時：使用 FormData 格式
        const formData = new FormData()

        // 添加 action
        formData.append('action', 'manualUpdate')

        // 添加基本客戶信息（所有資料都保存到 businessSpecificData）
        formData.append('name', requestData.name)
        formData.append('phone', requestData.phone)
        formData.append('email', requestData.email)
        formData.append('gender', requestData.gender)
        formData.append('birthdate', requestData.birthdate)
        formData.append('region', requestData.region)
        formData.append('notes', requestData.notes)
        formData.append('membershipLevel', requestData.membershipLevel)
        formData.append('totalSpent', requestData.totalSpent)
        formData.append('blacklist', requestData.blacklist)
        formData.append('tags', JSON.stringify(requestData.tags))

        // 添加刪除的照片
        if (hasDeletedPhotos) {
          formData.append('deletedPhotos', JSON.stringify(deletedPhotoIds))
        }

        // 添加新照片
        customerPhotos.forEach((photo) => {
          if (photo.originFileObj) {
            formData.append('photos', photo.originFileObj)
          }
        })

        // 處理照片上傳
        if (hasNewPhotos) {
          message.loading({
            content: '正在處理照片，請稍候...',
            key: 'photoUpload',
            duration: 0
          })
        }

        response = await api.put(
          `/api/business_B/${businessId}/customers/${customerId}`,
          formData
        )
      } else {
        // 沒有照片時：使用 JSON 格式
        response = await api.put(
          `/api/business_B/${businessId}/customers/${customerId}`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
      }

      if (response.data.success) {
        if (hasNewPhotos) {
          message.success({
            content: '照片上傳成功！',
            key: 'photoUpload',
            duration: 2
          })
        }
        message.success('客戶資料更新成功')

        // 更新客戶資料（PUT 回傳多為 data；與 GET 一致合併 photos 來源）
        const updatedCustomer = response.data.customer || response.data.data
        setCustomer(updatedCustomer)
        setEditedCustomer(null)
        setIsEditing(false)

        const savedPhotos =
          updatedCustomer?.photos ||
          updatedCustomer?.businessSpecificData?.photos ||
          []
        setCustomerPhotos(
          (Array.isArray(savedPhotos) ? savedPhotos : []).map(
            (photo, index) => ({
              uid: photo._id || `photo-${index}`,
              name: `photo-${index}`,
              status: 'done',
              url: `${PHOTO_URL}${photo.url}`
            })
          )
        )
      } else {
        message.error('更新失敗，請稍後再試')
      }
    } catch (error) {
      console.error('更新客戶資料失敗:', error)
      message.error('更新失敗，請稍後再試')
    } finally {
      setIsUploading(false)
    }
  }

  // 照片處理函數
  const handlePhotoChange = ({ fileList }) => {
    setCustomerPhotos(fileList)
  }

  // 性別轉換函數
  const translateGender = (gender) => {
    switch (gender) {
      case 'male':
        return '男'
      case 'female':
        return '女'
      default:
        return '未知'
    }
  }

  // 獲取會員等級列表
  const fetchMembershipTiers = async () => {
    if (!businessId) return

    setMembershipTiersLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-tiers`
      )
      if (response.data.success) {
        const tiersData = response.data.data?.tiers || []
        const enabledTiers = tiersData.filter((t) => t.isEnabled !== false)
        setMembershipTiers(enabledTiers)
      }
    } catch (error) {
      console.error('獲取會員等級列表失敗:', error)
    } finally {
      setMembershipTiersLoading(false)
    }
  }

  // 開啟會員等級調整 Modal（帶入目前資料）
  const openTierAdjustModal = () => {
    const data = customer?.businessSpecificData || {}
    const currentTier =
      data.membershipLevel || data.membershipTier || ''
    const expiresAt = data.tierExpiresAt
    const accStart = data.tierAccumulationStartDate

    tierAdjustForm.resetFields()
    tierAdjustForm.setFieldsValue({
      membershipLevel: currentTier,
      tierExpiresAt: expiresAt ? dayjs(expiresAt) : null,
      tierAccumulationStartDate: accStart ? dayjs(accStart) : null,
      note: '',
      resetAccumulated: false
    })
    setTierAdjustPermanent(!expiresAt)
    setTierAdjustOpen(true)
  }

  const submitTierAdjust = async () => {
    try {
      const values = await tierAdjustForm.validateFields()
      setTierAdjustSubmitting(true)
      const payload = {
        membershipLevel: values.membershipLevel,
        // 永久 → null；否則送 ISO
        tierExpiresAt: tierAdjustPermanent
          ? null
          : values.tierExpiresAt
            ? fromPicker(values.tierExpiresAt).toISOString()
            : null,
        // fromPicker：保留使用者挑的那一天。原本直接 toISOString() 會早 7~8 小時，
        // 後端以台灣午夜判斷到期時就會早一天失效。
        tierAccumulationStartDate: values.tierAccumulationStartDate
          ? fromPicker(values.tierAccumulationStartDate).toISOString()
          : undefined,
        note: values.note || '',
        resetAccumulated: !!values.resetAccumulated
      }
      await api.put(
        `/api/business_B/${businessId}/customers/${customerId}/tier`,
        payload
      )
      antdMessage.success('會員等級已調整')
      setTierAdjustOpen(false)
      // 重新載入客戶資料
      fetchCustomerDetails()
    } catch (err) {
      if (err?.errorFields) return // 表單驗證
      console.error(err)
      antdMessage.error(
        err?.response?.data?.message || '調整會員等級失敗'
      )
    } finally {
      setTierAdjustSubmitting(false)
    }
  }

  // 根據會員等級名稱獲取折扣率（支持舊的英文名稱和新的自定義名稱）
  const getMembershipDiscount = (tierName) => {
    if (!tierName) return 100

    // 檢查是否為舊的英文名稱（默認無折扣）
    const oldEnglishNames = ['regular', 'silver', 'gold', 'diamond', 'vip']
    if (oldEnglishNames.includes(tierName.toLowerCase())) {
      return 100 // 舊的英文名稱默認無折扣
    }

    // 從自定義會員等級中查找
    if (!membershipTiers.length) return 100
    const tier = membershipTiers.find((t) => t.name === tierName)
    return tier ? tier.discountRate : 100
  }

  // 獲取員工列表（用於顯示結帳信息）
  const fetchEmployees = async () => {
    if (!businessId) return
    try {
      const response = await api.get(`/api/employees/${businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  // 獲取結帳訂單信息
  const fetchCheckoutOrder = async (orderId) => {
    if (!orderId || !businessId) return
    setLoadingCheckout(true)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/checkout/orders/${orderId}`
      )
      const orderData = response.data?.data || response.data
      setCheckoutOrderData(orderData)
      setCheckoutModalVisible(true)
    } catch (error) {
      console.error('獲取結帳信息失敗:', error)
      message.error('獲取結帳信息失敗')
    } finally {
      setLoadingCheckout(false)
    }
  }

  // 處理預約點擊
  const handleBookingClick = (booking) => {
    if (booking.hasCheckedOut && booking.orderId) {
      // 已結帳：顯示結帳信息 Modal
      fetchCheckoutOrder(booking.orderId)
    } else {
      // 未結帳：導航到預約詳情頁
      navigate(`/bookings/${booking._id}`)
    }
  }

  // 格式化金額
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'NT$ 0'
    const numeric = Number(value) || 0
    return `NT$ ${numeric.toLocaleString()}`
  }

  // 獲取預約狀態顯示
  const getStatusDisplay = (status) => {
    const statusMap = {
      pending: { text: '等待確認', color: '#ff9500' },
      confirmed: { text: '已確認', color: '#34c759' },
      completed: { text: '已完成', color: '#007aff' },
      canceled: { text: '已取消', color: '#ff3b30' },
      no_show: { text: '未出席', color: '#af52de' }
    }
    return statusMap[status] || { text: status || '-', color: '#666' }
  }

  // 渲染服務明細（復用 CheckoutRecordsPage 的邏輯）
  const renderServices = (services = []) => {
    if (!services.length) {
      return <span>尚無服務紀錄</span>
    }

    // 處理第一劑/第二劑：將相同 serviceId 的第一劑和第二劑合併
    const processedServices = []
    const serviceMap = new Map()

    services.forEach((service) => {
      const isDoseType =
        service.doseType ||
        service.firstDosePersonnelId ||
        service.secondDosePersonnelId

      if (isDoseType && service.serviceId) {
        const key = service.serviceId
        if (!serviceMap.has(key)) {
          serviceMap.set(key, {
            serviceId: service.serviceId,
            name: service.name || service.serviceName || '服務項目',
            price: service.price,
            originalPrice: service.originalPrice,
            quantity: service.quantity,
            firstDosePersonnelId: null,
            firstDosePersonnelName: null,
            secondDosePersonnelId: null,
            secondDosePersonnelName: null
          })
        }

        const grouped = serviceMap.get(key)

        if (service.firstDosePersonnelId) {
          grouped.firstDosePersonnelId = service.firstDosePersonnelId
          grouped.firstDosePersonnelName =
            service.firstDosePersonnelName ||
            service.designerName ||
            service.personnelName ||
            null
        }

        if (service.secondDosePersonnelId) {
          grouped.secondDosePersonnelId = service.secondDosePersonnelId
          grouped.secondDosePersonnelName =
            service.secondDosePersonnelName ||
            service.designerName ||
            service.personnelName ||
            null
        }

        if (!grouped.price && service.price) {
          grouped.price = service.price
        }
        if (!grouped.originalPrice && service.originalPrice) {
          grouped.originalPrice = service.originalPrice
        }
        if (
          (grouped.quantity === undefined || grouped.quantity === null) &&
          service.quantity !== undefined &&
          service.quantity !== null
        ) {
          grouped.quantity = service.quantity
        }
      } else {
        processedServices.push(service)
      }
    })

    serviceMap.forEach((grouped) => {
      processedServices.push(grouped)
    })

    return (
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        {processedServices.map((service, index) => {
          const isDoseType =
            service.firstDosePersonnelId || service.secondDosePersonnelId
          const personnel = []

          if (isDoseType) {
            if (
              service.firstDosePersonnelId ||
              service.firstDosePersonnelName
            ) {
              const firstDoseName =
                service.firstDosePersonnelName ||
                (service.firstDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.firstDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (firstDoseName) {
                personnel.push({ type: '第一劑', name: firstDoseName })
              }
            }

            if (
              service.secondDosePersonnelId ||
              service.secondDosePersonnelName
            ) {
              const secondDoseName =
                service.secondDosePersonnelName ||
                (service.secondDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.secondDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (secondDoseName) {
                personnel.push({ type: '第二劑', name: secondDoseName })
              }
            }

            if (
              service.designerId &&
              service.designerId !== service.firstDosePersonnelId &&
              service.designerId !== service.secondDosePersonnelId
            ) {
              const designerName =
                service.designerName ||
                employees.find((e) => e._id === service.designerId)?.name ||
                null
              if (designerName) {
                personnel.push({ type: '設計師', name: designerName })
              }
            }

            if (service.assistantId) {
              const assistantName =
                service.assistantName ||
                employees.find((e) => e._id === service.assistantId)?.name ||
                null
              if (assistantName) {
                personnel.push({ type: '助理', name: assistantName })
              }
            }
          } else {
            const personnelIdValue =
              typeof service.personnelId === 'object' &&
              service.personnelId !== null
                ? service.personnelId._id
                : service.personnelId

            const designerId = service.designerId || personnelIdValue || null
            const designer = designerId
              ? employees.find((e) => e._id === designerId)
              : null
            const personnelNameValue =
              typeof service.personnelId === 'object' &&
              service.personnelId !== null
                ? service.personnelId.name
                : null
            const designerName =
              service.designerName ||
              service.personnelName ||
              personnelNameValue ||
              designer?.name ||
              null
            if (designerName) {
              personnel.push({ type: '設計師', name: designerName })
            }

            if (service.assistantId) {
              const assistantName =
                service.assistantName ||
                employees.find((e) => e._id === service.assistantId)?.name ||
                null
              if (assistantName) {
                personnel.push({ type: '助理', name: assistantName })
              }
            }
          }

          return (
            <li
              key={`${service.serviceId || index}-${index}`}
              style={{ marginBottom: 8 }}
            >
              <div style={{ marginBottom: 4 }}>
                <strong>
                  {service.name || service.serviceName || '服務項目'}
                </strong>{' '}
                {service.price !== undefined && (
                  <span>
                    {(() => {
                      const qty = Number(service.quantity) || 1
                      const unitPrice =
                        Number(service.originalPrice ?? service.price) || 0
                      const lineTotal = unitPrice * qty
                      if (qty > 1) {
                        return `${formatCurrency(unitPrice)} x${qty} = ${formatCurrency(lineTotal)}`
                      }
                      return formatCurrency(unitPrice)
                    })()}
                  </span>
                )}
              </div>
              {personnel.length > 0 && (
                <div
                  style={{
                    fontSize: 13,
                    color: '#576079',
                    marginTop: 6,
                    paddingLeft: 8,
                    lineHeight: 1.5
                  }}
                >
                  {personnel.map((p, pIdx) => (
                    <span
                      key={pIdx}
                      style={{
                        marginRight: 16,
                        display: 'inline-block'
                      }}
                    >
                      <strong style={{ color: '#0f2562' }}>{p.type}：</strong>
                      {p.name}
                    </span>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  // 渲染單一商品列
  const renderProductLine = (product, index) => (
    <li
      key={`${product.productId || index}-${index}`}
      style={{ marginBottom: 8 }}
    >
      <div style={{ marginBottom: 4 }}>
        <strong>{product.name || product.productName || '商品'}</strong>
        {product.quantity !== undefined && (
          <span> × {product.quantity}</span>
        )}
        {product.price !== undefined && (
          <span> - {formatCurrency(product.price)}</span>
        )}
        {product.totalPrice !== undefined && (
          <span> (小計: {formatCurrency(product.totalPrice)})</span>
        )}
        {product.isConsumable && (
          <Tag
            color='default'
            style={{ marginLeft: 8, fontSize: 11, lineHeight: '18px' }}
          >
            耗材
          </Tag>
        )}
      </div>
      {product.productSalesPersonnelName && (
        <div
          style={{
            fontSize: 13,
            color: '#576079',
            marginTop: 6,
            paddingLeft: 8,
            lineHeight: 1.5
          }}
        >
          <span>
            <strong style={{ color: '#0f2562' }}>銷售人員：</strong>
            {product.productSalesPersonnelName}
          </span>
        </div>
      )}
    </li>
  )

  // 商品明細小元件（含「顯示／隱藏耗材」切換，預設摺疊）。
  // 預設摺疊耗材，方便店家直接截圖給客人看。
  const ProductsSection = ({ products = [] }) => {
    const [showConsumables, setShowConsumables] = useState(false)
    if (!products.length) {
      return <span>尚無商品紀錄</span>
    }
    const consumables = products.filter((p) => p && p.isConsumable)
    const regulars = products.filter((p) => !(p && p.isConsumable))
    const visible = showConsumables ? products : regulars

    return (
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        {visible.map((product, index) => renderProductLine(product, index))}
        {regulars.length === 0 && consumables.length > 0 && !showConsumables && (
          <li
            style={{
              listStyle: 'none',
              color: '#888',
              fontSize: 13,
              paddingLeft: 0
            }}
          >
            本次共 {consumables.length} 項耗材，已隱藏
          </li>
        )}
        {consumables.length > 0 && (
          <li
            style={{
              listStyle: 'none',
              paddingLeft: 0,
              marginTop: 4,
              textAlign: 'right'
            }}
          >
            <Button
              type='link'
              size='small'
              icon={
                showConsumables ? <EyeInvisibleOutlined /> : <EyeOutlined />
              }
              onClick={() => setShowConsumables((v) => !v)}
            >
              {showConsumables
                ? '隱藏耗材'
                : `顯示耗材（${consumables.length}）`}
            </Button>
          </li>
        )}
      </ul>
    )
  }

  // 舊呼叫點仍走這個 wrapper，內部改用新元件
  const renderProducts = (products = []) => (
    <ProductsSection products={products} />
  )

  // 渲染付款方式
  const renderPaymentBreakdown = (payment = {}) => {
    const paymentMethodLabels = [
      { value: 'cash', label: '現金' },
      { value: 'storedValue', label: '儲值金' },
      { value: 'voucher', label: '票券' },
      { value: 'transfer', label: '匯款' },
      { value: 'deposit', label: '定金' },
      // 新增：刷卡、行動支付
      { value: 'cardPayment', label: '刷卡' },
      { value: 'mobilePayment', label: '行動支付' },
      { value: 'bonus', label: '紅利金' },
      { value: 'debt', label: '顧客欠款' },
      { value: 'installment', label: '分期' }
    ]

    const entries = paymentMethodLabels
      .map((item) => ({
        key: item.value,
        label: item.label,
        amount: Number(payment[item.value]) || 0
      }))
      .filter((entry) => entry.amount > 0)

    // 金流手續費（店家支出，單獨標示，不計入客戶付款總額）
    const paymentFee = Number(payment.paymentFee) || 0

    if (!entries.length && paymentFee === 0) {
      return <span>尚無付款紀錄</span>
    }

    return (
      <div style={{ display: 'grid', gap: 6 }}>
        {entries.map((entry) => (
          <div
            key={entry.key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 14
            }}
          >
            <span>{entry.label}</span>
            <span style={{ fontWeight: 600, color: '#0f2562' }}>
              {formatCurrency(entry.amount)}
            </span>
          </div>
        ))}
        {paymentFee > 0 && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: '1px dashed #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              color: '#ad6800'
            }}
            title='刷卡／行動支付由收單行或第三方支付扣除的手續費，屬店家支出，不計入客戶付款'
          >
            <span>金流手續費</span>
            <span style={{ fontWeight: 600 }}>
              − {formatCurrency(paymentFee)}
            </span>
          </div>
        )}
      </div>
    )
  }

  const renderCouponDeduction = (order = {}) => {
    const payment = order.payment || {}
    const couponDeduction = Number(payment.voucher) || 0
    const appliedCoupon = order.appliedStoreCoupon || order.appliedUserCoupon

    if (couponDeduction <= 0 && !appliedCoupon) return null

    return (
      <div
        style={{
          display: 'grid',
          gap: 6,
          fontSize: 14,
          color: '#576079'
        }}
      >
        <span>
          折抵金額：
          <strong style={{ color: '#0f2562' }}>
            {formatCurrency(couponDeduction)}
          </strong>
        </span>
        {appliedCoupon?.code && (
          <span>
            優惠碼：<strong style={{ color: '#0f2562' }}>{appliedCoupon.code}</strong>
          </span>
        )}
        {appliedCoupon?.description && (
          <span>
            說明：
            <strong style={{ color: '#0f2562' }}>{appliedCoupon.description}</strong>
          </span>
        )}
      </div>
    )
  }

  // 取得此顧客的堂票
  const fetchCustomerClassTickets = async () => {
    if (!businessId || !customerId) return
    setClassTicketsLoading(true)
    try {
      // 持有的票與使用歷程一起抓，兩者都失敗才算沒有堂票模組
      const [ticketRes, usageRes] = await Promise.all([
        listClassTicketsByCustomer(businessId, customerId),
        listClassTicketUsagesByCustomer(businessId, customerId, {
          limit: 50
        }).catch(() => null)
      ])
      const list = ticketRes?.data?.data || ticketRes?.data?.items || []
      setClassTickets(Array.isArray(list) ? list : [])
      const usages = usageRes?.data?.data || []
      setClassTicketUsages(Array.isArray(usages) ? usages : [])
    } catch (err) {
      // 沒開通堂票模組或 API 失敗時靜默
      setClassTickets([])
      setClassTicketUsages([])
    } finally {
      setClassTicketsLoading(false)
    }
  }

  /**
   * 抓「最新一份有問卷、且已簽署」的同意書作答。
   * 清單本身不含答案（答案在簽署存證裡），所以先撈清單挑出那一筆，再取詳情。
   */
  const fetchLatestConsentAnswers = useCallback(async () => {
    if (!businessId || !customerId || !consentEnabled) return
    try {
      const listRes = await listConsentFormsByCustomer(businessId, customerId)
      const rows = listRes?.data?.data || []
      const target = rows
        .filter((r) => r.status === 'signed' && r.questionCount > 0)
        .sort(
          (a, b) => new Date(b.signedAt || 0) - new Date(a.signedAt || 0)
        )[0]
      if (!target) {
        setLatestConsentAnswers(null)
        return
      }
      const detailRes = await getConsentFormDetail(businessId, target._id)
      const answers = detailRes?.data?.data?.signature?.answers || []
      setLatestConsentAnswers(
        answers.length
          ? {
              requestId: target._id,
              templateTitle: target.templateTitle,
              signedAt: target.signedAt,
              answers
            }
          : null
      )
    } catch (err) {
      // 問卷只是輔助資訊，抓不到就安靜地不顯示，不要擋住整個客戶頁
      console.warn('取得問卷作答失敗：', err?.message)
      setLatestConsentAnswers(null)
    }
  }, [businessId, customerId, consentEnabled])

  useEffect(() => {
    fetchLatestConsentAnswers()
  }, [fetchLatestConsentAnswers])

  // 初始化數據
  useEffect(() => {
    if (customerId) {
      setAllCustomerBookings(null)
      fetchCustomerDetails()
      fetchCustomerBalance()
      fetchTransactionHistory()
      fetchCustomerBookings()
      fetchCustomerGroups()
      fetchCustomerClassTickets()
    }
    if (businessId) {
      fetchMembershipTiers()
      fetchEmployees()
    }
  }, [customerId, businessId])

  if (loading) {
    return (
      <Container>
        <Spin
          size='large'
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '100px'
          }}
        />
      </Container>
    )
  }

  if (!customer) {
    return (
      <Container>
        <Empty description='客戶不存在' />
      </Container>
    )
  }

  const displayData = getCustomerDisplayData(customer)

  return (
    <Container>
      {/* 頁面標題 */}
      <Header>
        <BackButton
          onClick={goBack}
          icon={<ArrowLeftOutlined />}
        ></BackButton>
        <CustomerInfo>
          <CustomerAvatar
            src={getCustomerAvatarSrc(customer)}
            name={displayData.name}
            size={60}
            mobileSize={52}
            bordered
          />
          <CustomerDetails>
            <CustomerName title={displayData.name}>
              {displayData.name}
              {getLineDisplayNameSuffix(customer, displayData) ? (
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#8e8e93',
                    marginLeft: 6
                  }}
                >
                  {getLineDisplayNameSuffix(customer, displayData)}
                </span>
              ) : null}
            </CustomerName>
            <CustomerPhone title={displayData.phone}>
              {displayData.phone}
            </CustomerPhone>
          </CustomerDetails>
        </CustomerInfo>
        <HeaderActions>
          {isEditing ? (
            <>
              <Button onClick={handleCancel} disabled={isUploading}>
                取消
              </Button>
              <Button
                type='primary'
                onClick={handleSave}
                loading={isUploading}
                disabled={isUploading}
              >
                保存
              </Button>
            </>
          ) : (
            <>
              <Tooltip title='合併到其他顧客'>
                <Button
                  onClick={() => setMergeModalOpen(true)}
                  icon={<MergeCellsOutlined />}
                  disabled={isUploading}
                ></Button>
              </Tooltip>
              <Button
                danger
                onClick={handleDelete}
                icon={<DeleteOutlined />}
                disabled={isUploading}
              ></Button>
              <Button
                type='primary'
                onClick={handleEdit}
                icon={<EditOutlined />}
                disabled={isUploading}
              ></Button>
            </>
          )}
        </HeaderActions>
      </Header>

      <CustomerMergeModal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        onMerged={() => {
          // 合併完成後刷新頁面，保留方就是當前頁的客戶，重新讀資料
          fetchCustomerDetails?.()
          fetchCustomerBookings?.()
        }}
        businessId={businessId}
        currentCustomer={customer}
      />

      {/* 統計信息 */}
      <StatsSection>
        <StatsGrid>
          <StatItem>
            <Statistic
              title='完成預約數'
              value={customer.visitStats?.completedBookings || 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </StatItem>

          <StatItem>
            <Statistic
              title='出席率'
              value={customer.visitStats?.attendanceRate || 0}
              suffix='%'
              prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
            />
          </StatItem>

          <StatItem>
            <Statistic
              title='平均消費'
              value={Math.ceil(
                Number(customer.visitStats?.averageSpending) || 0
              )}
              prefix={<DollarOutlined style={{ color: '#faad14' }} />}
              precision={0}
              formatter={(value) => `NT$${value}`}
            />
          </StatItem>

          <StatItem>
            <Statistic
              title='未出席次數'
              value={customer.visitStats?.noShowCount || 0}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            />
          </StatItem>

          <StatItem>
            <Statistic
              title='取消次數'
              value={customer.visitStats?.cancelledBookings || 0}
              prefix={<CloseCircleOutlined style={{ color: '#ff7a45' }} />}
            />
          </StatItem>

          <StatItem>
            <Statistic
              title='最後到店'
              value={
                customer.visitStats?.lastCompletedDate
                  ? dayjs(customer.visitStats.lastCompletedDate).format('MM/DD')
                  : '-'
              }
              prefix={<CalendarOutlined style={{ color: '#13c2c2' }} />}
            />
          </StatItem>
        </StatsGrid>
      </StatsSection>

      {/* 主要內容 */}
      <ContentGrid>
        {/* 左側：客戶信息 */}
        <div>
          <InfoCard>
            <SectionTitle>客戶信息</SectionTitle>
            <InfoItem>
              <InfoLabel>姓名</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Input
                    value={editedCustomer?.businessSpecificData?.name || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          name: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入姓名'
                  />
                ) : (
                  displayData.name
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>電話</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Input
                    value={editedCustomer?.businessSpecificData?.phone || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          phone: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入電話'
                  />
                ) : (
                  displayData.phone
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Input
                    value={editedCustomer?.businessSpecificData?.email || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          email: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入Email'
                  />
                ) : (
                  displayData.email
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                會員等級
                {!isEditing && (
                  <Button
                    type='link'
                    size='small'
                    icon={<EditOutlined />}
                    onClick={() => openTierAdjustModal()}
                    style={{
                      marginLeft: 4,
                      padding: '0 4px',
                      fontSize: 12
                    }}
                  >
                    調整
                  </Button>
                )}
              </InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Space direction='vertical' style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      loading={membershipTiersLoading}
                      value={
                        editedCustomer?.businessSpecificData?.membershipLevel ||
                        editedCustomer?.businessSpecificData?.membershipTier ||
                        ''
                      }
                      onChange={(tierName) => {
                        setEditedCustomer({
                          ...editedCustomer,
                          businessSpecificData: {
                            ...editedCustomer.businessSpecificData,
                            membershipLevel: tierName
                          }
                        })
                      }}
                      placeholder='請選擇會員等級'
                      allowClear
                    >
                      {/*
                        舊的英文名稱選項 —— 只在店家「還沒建自己的等級表」時才出現。
                        這幾個 Option 的 value 是小寫英文（'vip'），跟店家實際的
                        等級名稱（'VIP會員'）對不上，選到就是一筆查不到等級的
                        髒資料（折扣抓不到、到期評估會跳過這個人）。
                      */}
                      {membershipTiers.length === 0 && (
                        <>
                          <Select.Option value='Regular'>普通會員</Select.Option>
                          <Select.Option value='Silver'>銀卡會員</Select.Option>
                          <Select.Option value='Gold'>金卡會員</Select.Option>
                          <Select.Option value='Diamond'>鑽石會員</Select.Option>
                          <Select.Option value='VIP'>VIP會員</Select.Option>
                        </>
                      )}
                      {/* 自定義會員等級選項 */}
                      {membershipTiers.map((tier) => (
                        <Select.Option key={tier._id} value={tier.name}>
                          <TagBadge
                            style={{
                              background: tier.color || '#1890ff',
                              color: tier.color === '#ffd700' ? '#000' : '#fff',
                              border: 'none'
                            }}
                          >
                            {tier.name}
                          </TagBadge>{' '}
                          {tier.discountRate}%
                          {tier.discountRate === 100
                            ? '（無折扣）'
                            : `（${tier.discountRate}折）`}
                        </Select.Option>
                      ))}
                    </Select>
                    {(editedCustomer?.businessSpecificData?.membershipLevel ||
                      editedCustomer?.businessSpecificData?.membershipTier) && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        當前折扣：
                        {getMembershipDiscount(
                          editedCustomer.businessSpecificData.membershipLevel ||
                            editedCustomer.businessSpecificData.membershipTier
                        )}
                        %
                        {getMembershipDiscount(
                          editedCustomer.businessSpecificData.membershipLevel ||
                            editedCustomer.businessSpecificData.membershipTier
                        ) === 100
                          ? '（無折扣）'
                          : `（${getMembershipDiscount(
                              editedCustomer.businessSpecificData
                                .membershipLevel ||
                                editedCustomer.businessSpecificData
                                  .membershipTier
                            )}折）`}
                      </div>
                    )}
                  </Space>
                ) : displayData.membershipLevel ||
                  displayData.membershipTier ? (
                  <Space direction='vertical' size='small'>
                    {(() => {
                      const membershipLevel =
                        displayData.membershipLevel ||
                        displayData.membershipTier

                      // 解析顯示名稱與顏色：
                      // 1. 優先：商家現有等級表（自訂名稱直接顯示）
                      // 2. fallback：舊版英文預設名稱對應到中文（向下相容）
                      const directMatch = membershipTiers.find(
                        (t) => t.name === membershipLevel
                      )
                      const oldEnglishNames = {
                        regular: '普通會員',
                        silver: '銀卡會員',
                        gold: '金卡會員',
                        diamond: '鑽石會員',
                        vip: 'VIP會員'
                      }
                      const lowerLevel = membershipLevel?.toLowerCase()
                      const legacyName = oldEnglishNames[lowerLevel]

                      let displayName
                      let tierColor
                      let discountRate
                      if (directMatch) {
                        // 自訂等級直接用
                        displayName = directMatch.name
                        tierColor = directMatch.color || '#1890ff'
                        discountRate = directMatch.discountRate
                      } else if (legacyName) {
                        // 舊資料 fallback
                        displayName = legacyName
                        tierColor = membershipColors[legacyName]
                        discountRate = 100
                      } else {
                        // 未知值：原樣顯示
                        displayName = membershipLevel
                        tierColor = '#1890ff'
                        discountRate = getMembershipDiscount(membershipLevel)
                      }

                      // 會籍到期日 + 累計金額
                      // 後端 getCustomerDetail 已將當前商家的資料平鋪成 customer.businessSpecificData，
                      // 直接讀即可（找不到時退回 businessRelations 找對應 businessId 的那筆作備援）
                      const tierData =
                        customer?.businessSpecificData ||
                        (customer?.businessRelations || []).find(
                          (r) =>
                            r &&
                            r.businessId &&
                            String(r.businessId) === String(businessId)
                        )?.businessSpecificData ||
                        {}
                      const expiresAt = tierData.tierExpiresAt
                      const accumulated = tierData.tierAccumulatedAmount || 0
                      const assignedAt = tierData.tierAssignedAt
                      const daysToExpire = expiresAt
                        ? Math.max(
                            0,
                            dayjs(expiresAt).diff(tzNow(), 'day')
                          )
                        : null

                      return (
                        <>
                          <MembershipBadge
                            style={{
                              background: tierColor,
                              color:
                                tierColor === '#ffd700' ||
                                tierColor?.includes('#ffd700')
                                  ? '#000'
                                  : '#fff'
                            }}
                          >
                            {displayName}
                          </MembershipBadge>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            折扣：{discountRate}%
                            {discountRate === 100
                              ? '（無折扣）'
                              : `（${discountRate}折）`}
                          </div>
                          {assignedAt && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              升等日：
                              {dayjs(assignedAt).format('YYYY/MM/DD')}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: '#666' }}>
                            會籍到期：
                            {expiresAt ? (
                              <>
                                <Text strong>
                                  {formatTz(expiresAt, 'YYYY/MM/DD')}
                                </Text>
                                {daysToExpire != null && (
                                  <Tag
                                    color={
                                      daysToExpire <= 30 ? 'orange' : 'default'
                                    }
                                    style={{ marginLeft: 6 }}
                                  >
                                    剩 {daysToExpire} 天
                                  </Tag>
                                )}
                              </>
                            ) : (
                              <Tag color='blue' style={{ marginLeft: 4 }}>
                                永久
                              </Tag>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            累計實付
                            <Tooltip
                              placement='right'
                              title={
                                <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                                  <div style={{ marginBottom: 6 }}>
                                    <b>什麼是累計實付金額？</b>
                                  </div>
                                  <div style={{ marginBottom: 6 }}>
                                    會員從目前等級被授予以來，在本店實際付出的金額總和（不含
                                    儲值金抵扣，已扣優惠券）。
                                  </div>
                                  <div style={{ marginBottom: 6 }}>
                                    <b>用來幹嘛？</b>
                                  </div>
                                  <div>
                                    自動升等／續等／降等的判斷依據；客戶端的「距下一級還差
                                    NT$ XXX」進度條也是用它計算。
                                  </div>
                                </div>
                              }
                              overlayStyle={{ maxWidth: 320 }}
                            >
                              <QuestionCircleOutlined
                                style={{
                                  marginLeft: 4,
                                  color: '#999',
                                  cursor: 'help'
                                }}
                              />
                            </Tooltip>
                            ：
                            <Text strong>
                              NT$ {accumulated.toLocaleString()}
                            </Text>
                          </div>
                        </>
                      )
                    })()}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>未設置</span>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>性別</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Radio.Group
                    value={editedCustomer?.businessSpecificData?.gender || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          gender: e.target.value
                        }
                      })
                    }
                  >
                    <Radio value='male'>男</Radio>
                    <Radio value='female'>女</Radio>
                  </Radio.Group>
                ) : (
                  translateGender(displayData.gender)
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>生日</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <DatePicker
                    value={
                      editedCustomer?.businessSpecificData?.birthdate
                        ? dayjs(editedCustomer.businessSpecificData.birthdate)
                        : null
                    }
                    onChange={(date) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          birthdate: date ? date.format('YYYY-MM-DD') : null
                        }
                      })
                    }
                    style={{ width: '100%' }}
                  />
                ) : displayData.birthdate ? (
                  dayjs(displayData.birthdate).format('YYYY/MM/DD')
                ) : (
                  '未知'
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>紅利點數</InfoLabel>
              <InfoValue>{pointsBalance ?? displayData.points}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>總消費</InfoLabel>
              <InfoValue>
                NT$ {displayData.totalSpent?.toLocaleString() || 0}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>平台帳號</InfoLabel>
              <InfoValue>
                {displayData.hasPlatformAccount ? (
                  <MorandiPlatformLinkedTag>
                    已連結 Beautefeel
                  </MorandiPlatformLinkedTag>
                ) : (
                  <MorandiStatusMutedTag>未連結</MorandiStatusMutedTag>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>本店 LINE 官方帳號</InfoLabel>
              <InfoValue>
                {!displayData.hasPlatformAccount ? (
                  <Text type='secondary'>
                    客戶連結平台帳號，並配對本店官方 LINE 後，此處會顯示已加入
                  </Text>
                ) : displayData.hasOfficialLineAccountPaired ? (
                  <Space direction='vertical' size={4}>
                    <MorandiLinePairedTag>
                    已可接收本店 LINE 推播
                    </MorandiLinePairedTag>
                    {displayData.officialLineAccountPairedAt ? (
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        紀錄時間：{' '}
                        {dayjs(displayData.officialLineAccountPairedAt).format(
                          'YYYY/MM/DD HH:mm'
                        )}
                      </Text>
                    ) : null}
                  </Space>
                ) : (
                  <MorandiStatusMutedTag>尚未加入</MorandiStatusMutedTag>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>標籤</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Select
                    mode='tags'
                    style={{ width: '100%' }}
                    placeholder='請選擇或輸入標籤'
                    value={editedCustomer?.businessSpecificData?.tags || []}
                    onChange={(tags) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          tags
                        }
                      })
                    }
                    options={tagOptions.map((tag) => ({
                      label: tag,
                      value: tag
                    }))}
                  />
                ) : displayData.tags && displayData.tags.length > 0 ? (
                  <TagsWrapper>
                    {displayData.tags.map((tag, index) => (
                      <TagBadge key={index}>{tag}</TagBadge>
                    ))}
                  </TagsWrapper>
                ) : (
                  '無標籤'
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>地址</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Input
                    value={editedCustomer?.businessSpecificData?.region || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          region: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入地址（選填）'
                  />
                ) : (
                  displayData.region || '—'
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>備註</InfoLabel>
              <InfoValue>
                {isEditing ? (
                  <Input.TextArea
                    rows={3}
                    value={editedCustomer?.businessSpecificData?.notes || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          notes: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入備註'
                  />
                ) : (
                  displayData.notes || '無備註'
                )}
              </InfoValue>
            </InfoItem>

            {/* 照片管理 */}
            <InfoItem>
              {(() => {
                const viewPhotos =
                  customer.photos ||
                  customer.businessSpecificData?.photos ||
                  []
                const totalCount = isEditing
                  ? customerPhotos.length
                  : viewPhotos.length + servicePhotoTotal
                return (
                  <>
                    <PhotoSectionHeader>
                      <InfoLabel style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                        照片記錄
                        {totalCount > 0 && (
                          <PhotoCountBadge>{totalCount}</PhotoCountBadge>
                        )}
                      </InfoLabel>
                    </PhotoSectionHeader>
                    <InfoValue style={{ marginTop: 0 }}>
                      <PhotoWall>
                        {isEditing ? (
                          <Upload
                            listType='picture-card'
                            fileList={customerPhotos}
                            onChange={handlePhotoChange}
                            beforeUpload={() => false}
                            multiple
                            showUploadList={{
                              showRemoveIcon: true,
                              showPreviewIcon: true
                            }}
                          >
                            {customerPhotos.length >= 20 ? null : (
                              <div style={{ textAlign: 'center' }}>
                                <PlusOutlined style={{ fontSize: 18, color: '#d5728a' }} />
                                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                                  新增
                                </div>
                              </div>
                            )}
                          </Upload>
                        ) : viewPhotos.length > 0 ? (
                          (() => {
                            const MAX_PREVIEW = 8
                            const showOverflow =
                              viewPhotos.length > MAX_PREVIEW
                            const visiblePhotos = showOverflow
                              ? viewPhotos.slice(0, MAX_PREVIEW)
                              : viewPhotos
                            const overflowIndex = MAX_PREVIEW - 1
                            const overflowCount =
                              viewPhotos.length - MAX_PREVIEW
                            return (
                              <Image.PreviewGroup>
                                <PhotoGrid>
                                  {visiblePhotos.map((photo, idx) => {
                                    const url = `${PHOTO_URL}${photo.url}`
                                    const isOverflowTile =
                                      showOverflow && idx === overflowIndex
                                    return (
                                      <PhotoTile key={photo._id || idx}>
                                        <Image
                                          src={url}
                                          alt={`照片 ${idx + 1}`}
                                          preview={
                                            isOverflowTile
                                              ? false
                                              : { src: url }
                                          }
                                        />
                                        {isOverflowTile && (
                                          <PhotoOverflow
                                            onClick={(e) => {
                                              // 點蒙版時打開最後一張的預覽（PreviewGroup 會帶出剩餘）
                                              e.stopPropagation()
                                              const imgEl =
                                                e.currentTarget.parentElement?.querySelector(
                                                  '.ant-image-img'
                                                )
                                              imgEl?.click()
                                            }}
                                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                          >
                                            <span className='overflow-num'>
                                              +{overflowCount}
                                            </span>
                                            <span className='overflow-hint'>
                                              查看全部
                                            </span>
                                          </PhotoOverflow>
                                        )}
                                      </PhotoTile>
                                    )
                                  })}
                                  {/* 其餘照片塞進 PreviewGroup 但不渲染 tile，讓使用者左右滑覽看到全部 */}
                                  {showOverflow && (
                                    <div style={{ display: 'none' }}>
                                      {viewPhotos
                                        .slice(MAX_PREVIEW)
                                        .map((photo, idx) => (
                                          <Image
                                            key={photo._id || `extra-${idx}`}
                                            src={`${PHOTO_URL}${photo.url}`}
                                            alt={`照片 ${MAX_PREVIEW + idx + 1}`}
                                          />
                                        ))}
                                    </div>
                                  )}
                                </PhotoGrid>
                              </Image.PreviewGroup>
                            )
                          })()
                        ) : servicePhotoTotal > 0 ? null : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description='暫無照片'
                            style={{ margin: '20px 0' }}
                          />
                        )}
                        {/* 結帳時上傳的施作照片：依每次結帳分組，帶日期與結帳備註。
                            唯讀 —— 要改照片請到預約詳情。編輯模式下也照樣顯示，
                            免得店家以為這些照片要在這裡重傳。 */}
                        <CustomerServicePhotoRecords
                          businessId={businessId}
                          customerId={customerId}
                          onLoaded={setServicePhotoTotal}
                        />
                      </PhotoWall>
                    </InfoValue>
                  </>
                )
              })()}
            </InfoItem>
          </InfoCard>

          {/* 所屬群組 */}
          {(customerGroupsLoading || customerGroups.length > 0) && (
            <InfoCard style={{ marginTop: '24px' }}>
              <SectionTitle>所屬群組</SectionTitle>
              {customerGroupsLoading ? (
                <Spin size='small' />
              ) : customerGroups.length > 0 ? (
                <TagsWrapper>
                  {customerGroups.map((g) => (
                    <TagBadge
                      key={g._id}
                      style={{
                        cursor: 'pointer',
                        marginBottom: 8,
                        padding: '6px 12px'
                      }}
                      onClick={() => navigate(`/customers/groups/${g._id}`)}
                    >
                      {g.name}
                    </TagBadge>
                  ))}
                </TagsWrapper>
              ) : null}
            </InfoCard>
          )}

          {latestConsentAnswers && (
            <InfoCard>
              <SectionTitle>
                <span>最近問卷作答</span>
                <Button
                  type='link'
                  size='small'
                  icon={<RightOutlined />}
                  onClick={() =>
                    window.open(
                      consentFormPrintableUrl(
                        businessId,
                        latestConsentAnswers.requestId
                      ),
                      '_blank',
                      'noopener'
                    )
                  }
                  style={{ padding: 0, height: 'auto' }}
                >
                  完整同意書
                </Button>
              </SectionTitle>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {latestConsentAnswers.templateTitle}
                {latestConsentAnswers.signedAt
                  ? `・${dayjs(latestConsentAnswers.signedAt).format(
                      'YYYY/MM/DD'
                    )} 簽署`
                  : ''}
              </Text>
              <div style={{ marginTop: 12 }}>
                <ConsentAnswerList answers={latestConsentAnswers.answers} />
              </div>
            </InfoCard>
          )}

        </div>

        {/* 右側：客戶資產 tabs（儲值金 / 堂票 / 常做項目） */}
        <div>
          {installmentSummary?.activeCount > 0 && assetTab !== 'installment' && (
            <Hint
              tone={
                installmentSummary.overdue > 0 || installmentSummary.dueNow > 0
                  ? 'warning'
                  : 'info'
              }
              title={`這位顧客有 ${installmentSummary.activeCount} 筆分期尚未結清`}
              action={
                <Button size='small' onClick={() => setAssetTab('installment')}>
                  查看分期／收款
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              未收 NT$ {Math.round(installmentSummary.outstanding || 0).toLocaleString()}
              {installmentSummary.dueNow > 0 &&
                ` · 本次應繳 NT$ ${Math.round(installmentSummary.dueNow).toLocaleString()}`}
              {installmentSummary.overdue > 0 &&
                ` · 逾期 NT$ ${Math.round(installmentSummary.overdue).toLocaleString()}`}
            </Hint>
          )}
          <TabbedAssetWrap>
            <PillTabs
              activeKey={assetTab}
              onChange={(key) => {
                setAssetTab(key)
                // 記住這家店習慣先看哪一頁，下次直接開在那裡
                try {
                  localStorage.setItem(CUSTOMER_ASSET_TAB_KEY, key)
                } catch (e) {
                  // storage 被封鎖時就只是不記住，功能照常
                }
              }}
              color='#d5728a'
              /* 順序與 ASSET_TAB_KEYS 一致，堂票排第一 */
              items={[
                {
                  key: 'classTicket',
                  label: '堂票',
                  icon: <IdcardOutlined />,
                  count: classTickets.length || undefined
                },
                {
                  key: 'storedValue',
                  label: '儲值金',
                  icon: <WalletOutlined />
                },
                ...(pointsTabEnabled
                  ? [
                      {
                        key: 'points',
                        label: '紅利點數',
                        icon: <GiftOutlined />
                      }
                    ]
                  : []),
                ...(installmentTabEnabled
                  ? [
                      {
                        key: 'installment',
                        label: '分期',
                        icon: <CreditCardOutlined />,
                        count: installmentSummary?.activeCount || undefined
                      }
                    ]
                  : []),
                {
                  key: 'frequentItems',
                  label: '常做項目',
                  icon: <StarOutlined />,
                  count:
                    customer?.visitStats?.frequentServices?.length || undefined
                }
              ]}
            />

            {assetTab === 'storedValue' && (
            <>
          <InfoCard>
            <SectionTitle>
              <span>儲值金餘額</span>
              <Button
                type='link'
                size='small'
                icon={<RightOutlined />}
                onClick={() =>
                  navigate(`/value-added/records?customerId=${customerId}`)
                }
                style={{ padding: 0, height: 'auto' }}
              >
                儲值紀錄查詢
              </Button>
            </SectionTitle>
            {balanceLoading ? (
              <Spin />
            ) : balanceInfo ? (
              <BalanceCard>
                <BalanceAmount>
                  NT$ {balanceInfo.balance?.toLocaleString() || 0}
                </BalanceAmount>
                <BalanceLabel>可用餘額</BalanceLabel>
                {balanceInfo.balanceByScheme?.length > 0 && (
                  <SchemeBalanceSection>
                    <SchemeBalanceTitle>各儲值活動餘額</SchemeBalanceTitle>
                    <SchemeBalanceList>
                      {balanceInfo.balanceByScheme.map((s) => (
                        <SchemeBalanceItem key={s.schemeKey}>
                          <SchemeBalanceName>{s.schemeKey}</SchemeBalanceName>
                          <SchemeBalanceAmount>
                            NT$ {Number(s.balance || 0).toLocaleString()}
                          </SchemeBalanceAmount>
                          {(s.expiryDateDisplay || s.expiryDate) && (
                            <SchemeBalanceExpiry>
                              期限{' '}
                              {s.expiryDateDisplay ||
                                dayjs(s.expiryDate).format('YYYY/MM/DD')}
                            </SchemeBalanceExpiry>
                          )}
                        </SchemeBalanceItem>
                      ))}
                    </SchemeBalanceList>
                  </SchemeBalanceSection>
                )}
                <BalanceActions>
                  <Tooltip title='為客戶增加儲值金額，可用於未來消費'>
                    <BalanceButton
                      onClick={() => handleBalanceOperation('topup')}
                    >
                      儲值
                    </BalanceButton>
                  </Tooltip>
                  <Tooltip title='扣除客戶儲值金額，用於消費或服務付款'>
                    <BalanceButton
                      onClick={() => handleBalanceOperation('deduct')}
                    >
                      扣款
                    </BalanceButton>
                  </Tooltip>
                  <Tooltip title='退還客戶已扣款的儲值金額，用於取消訂單或退費'>
                    <BalanceButton
                      onClick={() => handleBalanceOperation('refund')}
                    >
                      退款
                    </BalanceButton>
                  </Tooltip>
                  <Tooltip title='手動調整客戶儲值金餘額，請謹慎使用並填寫調整原因'>
                    <BalanceButton
                      onClick={() => handleBalanceOperation('adjust')}
                    >
                      調整
                    </BalanceButton>
                  </Tooltip>
                </BalanceActions>
              </BalanceCard>
            ) : (
              <Empty description='暫無儲值金記錄' />
            )}
          </InfoCard>

          {/* 交易歷史 */}
          <InfoCard style={{ marginTop: '24px' }}>
            <SectionTitle>
              <span>交易歷史</span>
              <Button
                type='link'
                size='small'
                icon={<RightOutlined />}
                onClick={() =>
                  navigate(`/value-added/records?customerId=${customerId}`)
                }
                style={{ padding: 0, height: 'auto' }}
              >
                儲值紀錄查詢
              </Button>
            </SectionTitle>
            {historyLoading ? (
              <Spin />
            ) : transactionHistory.length > 0 ? (
              <TransactionList>
                {transactionHistory.map((transaction) => (
                  <TransactionItem key={transaction._id}>
                    <TransactionInfo>
                      <Space size={6} wrap>
                        <TransactionType type={transaction.type}>
                          {transaction.type === 'topup' && '儲值'}
                          {transaction.type === 'deduct' && '扣款'}
                          {transaction.type === 'refund' && '退款'}
                          {transaction.type === 'adjustment' && '調整'}
                        </TransactionType>
                        {transaction.schemeKey && (
                          <Tag color='purple'>
                            儲值活動：{transaction.schemeKey}
                          </Tag>
                        )}
                      </Space>
                      <TransactionAmount
                        amount={
                          transaction.type === 'deduct'
                            ? -Math.abs(transaction.amount)
                            : transaction.type === 'adjustment'
                              ? transaction.amount
                              : Math.abs(transaction.amount)
                        }
                      >
                        {transaction.type === 'deduct'
                          ? '-'
                          : transaction.type === 'adjustment'
                            ? transaction.amount >= 0
                              ? '+'
                              : '-'
                            : '+'}
                        NT$ {Math.abs(transaction.amount).toLocaleString()}
                      </TransactionAmount>
                    </TransactionInfo>
                    <TransactionDetails>
                      <TransactionNote>{transaction.note}</TransactionNote>
                      {transaction.balanceAfter != null && (
                        <TransactionTime>
                          餘額：NT${' '}
                          {Number(transaction.balanceAfter).toLocaleString()}
                        </TransactionTime>
                      )}
                      {transaction.type === 'deduct' &&
                        (transaction.bookingDate ||
                          transaction.bookingTime) && (
                          <TransactionTime>
                            預約日期：
                            {[transaction.bookingDate, transaction.bookingTime]
                              .filter(Boolean)
                              .join(' ')}
                          </TransactionTime>
                        )}
                      <TransactionMetaRow>
                        <TransactionTime>
                          操作時間：
                          {formatTz(
                            transaction.createdAt,
                            'YYYY/MM/DD HH:mm'
                          )}
                        </TransactionTime>
                        <TransactionOperator>
                          操作者：{transaction.operator?.name || '商家'}
                        </TransactionOperator>
                      </TransactionMetaRow>
                    </TransactionDetails>
                  </TransactionItem>
                ))}
              </TransactionList>
            ) : (
              <Empty description='暫無交易記錄' />
            )}
          </InfoCard>
            </>
            )}

            {assetTab === 'classTicket' && (
              <InfoCard>
                <SectionTitle>
                  <span>堂票紀錄</span>
                  {classTickets.length > 0 && (
                    <Button
                      type='link'
                      size='small'
                      icon={<RightOutlined />}
                      onClick={() =>
                        navigate(
                          `/class-ticket/records?customerId=${customerId}`
                        )
                      }
                      style={{ padding: 0, height: 'auto' }}
                    >
                      查看全部 {classTickets.length} 張
                    </Button>
                  )}
                </SectionTitle>
                {classTicketsLoading ? (
                  <Spin />
                ) : classTickets.length === 0 ? (
                  <Empty description='此客戶尚無堂票' />
                ) : (
                  <ClassTicketList>
                    {classTickets.slice(0, 5).map((t) => {
                      const statusMeta = {
                        active: {
                          text: '可使用',
                          bg: '#e8f7ee',
                          color: '#27ae60'
                        },
                        used_up: {
                          text: '使用完畢',
                          bg: '#eef0f3',
                          color: '#7f8c8d'
                        },
                        expired: {
                          text: '已過期',
                          bg: '#fdf2e6',
                          color: '#e67e22'
                        },
                        voided: {
                          text: '已作廢',
                          bg: '#fdecea',
                          color: '#e74c3c'
                        }
                      }[t.status] || {
                        text: t.status,
                        bg: '#eef0f3',
                        color: '#7f8c8d'
                      }
                      const ratio =
                        t.totalCount > 0
                          ? Math.max(
                              0,
                              Math.min(
                                1,
                                (t.totalCount - t.remainingCount) /
                                  t.totalCount
                              )
                            )
                          : 0
                      return (
                        <ClassTicketCard
                          key={t._id}
                          onClick={() =>
                            navigate(
                              `/class-ticket/records?ticketCode=${encodeURIComponent(t.ticketCode || '')}`
                            )
                          }
                        >
                          <ClassTicketTopRow>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <ClassTicketName>
                                {t.templateName}
                              </ClassTicketName>
                              <ClassTicketCode>
                                {t.ticketCode}
                              </ClassTicketCode>
                            </div>
                            <ClassTicketStatusTag
                              $bg={statusMeta.bg}
                              $color={statusMeta.color}
                            >
                              {statusMeta.text}
                            </ClassTicketStatusTag>
                          </ClassTicketTopRow>
                          <ClassTicketProgressWrap>
                            <ClassTicketProgressBg>
                              <ClassTicketProgressFill
                                $ratio={ratio}
                                $color={
                                  t.status === 'voided'
                                    ? '#bdc3c7'
                                    : '#d5728a'
                                }
                              />
                            </ClassTicketProgressBg>
                            <ClassTicketProgressText>
                              <b>{t.remainingCount}</b> / {t.totalCount}
                            </ClassTicketProgressText>
                          </ClassTicketProgressWrap>
                          <ClassTicketFooter>
                            <CalendarOutlined />
                            有效至{' '}
                            {formatTz(t.expiresAt, 'YYYY/MM/DD', '—')}
                          </ClassTicketFooter>
                        </ClassTicketCard>
                      )
                    })}
                  </ClassTicketList>
                )}

                {/* 使用歷程：所有票混在同一條時間線上，由新到舊 */}
                {classTicketUsages.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <SectionTitle style={{ marginBottom: 4 }}>
                      <span>使用歷程</span>
                    </SectionTitle>
                    <UsageTimeline>
                      {classTicketUsages.slice(0, 12).map((u) => {
                        const meta = {
                          redeem: { text: '核銷', color: '#d5728a' },
                          refund: { text: '退回', color: '#27ae60' },
                          void: { text: '作廢', color: '#e74c3c' },
                          issue: { text: '發放', color: '#6a859e' }
                        }[u.action] || { text: u.action, color: '#8c96a5' }
                        const from =
                          u.reason === 'checkout'
                            ? '結帳'
                            : u.reason === 'booking'
                              ? '預約'
                              : '手動'
                        return (
                          <UsageRow key={u._id}>
                            <UsageDot $color={meta.color} />
                            <UsageBody>
                              <UsageTop>
                                <b style={{ color: meta.color }}>
                                  {meta.text}
                                  {u.count > 1 ? ` ${u.count} 堂` : ''}
                                </b>
                                <span>{u.templateName || '堂票'}</span>
                                {u.serviceName && (
                                  <span style={{ color: '#666' }}>
                                    · {u.serviceName}
                                  </span>
                                )}
                              </UsageTop>
                              <UsageMeta>
                                <span>
                                  {formatTz(
                                    u.createdAt,
                                    'YYYY/MM/DD HH:mm'
                                  )}
                                </span>
                                {u.ticketCode && <span>{u.ticketCode}</span>}
                                {u.remainingAfter != null && (
                                  <span>剩 {u.remainingAfter} 堂</span>
                                )}
                                <span>來源：{from}</span>
                              </UsageMeta>
                            </UsageBody>
                          </UsageRow>
                        )
                      })}
                    </UsageTimeline>
                    {classTicketUsages.length > 12 && (
                      <Button
                        type='link'
                        size='small'
                        onClick={() =>
                          navigate(
                            `/class-ticket/records?customerId=${customerId}&tab=usages`
                          )
                        }
                        style={{ padding: 0, height: 'auto', marginTop: 8 }}
                      >
                        查看全部 {classTicketUsages.length} 筆
                      </Button>
                    )}
                  </div>
                )}
              </InfoCard>
            )}

            {assetTab === 'frequentItems' && (
              <InfoCard>
                <SectionTitle>常做項目</SectionTitle>
                {customer?.visitStats?.frequentServices?.length > 0 ? (
                  <FrequentList>
                    {customer.visitStats.frequentServices.map((s, i) => (
                      <FrequentItem key={i}>
                        <FrequentRank>{i + 1}</FrequentRank>
                        <FrequentName>{s.productName}</FrequentName>
                        <FrequentCount>{s.count} 次</FrequentCount>
                      </FrequentItem>
                    ))}
                  </FrequentList>
                ) : (
                  <Empty description='此客戶尚無常做項目資料' />
                )}
              </InfoCard>
            )}
            {assetTab === 'points' && pointsTabEnabled && (
              <InfoCard>
                <SectionTitle>
                  <span>紅利點數</span>
                </SectionTitle>
                <CustomerPointsPanel
                  businessId={businessId}
                  customerId={customerId}
                  onChanged={(b) => {
                    if (typeof b === 'number') setPointsBalance(b)
                  }}
                />
              </InfoCard>
            )}
            {assetTab === 'installment' && installmentTabEnabled && (
              <InfoCard>
                <SectionTitle>
                  <span>分期付款</span>
                </SectionTitle>
                <CustomerInstallmentsPanel
                  customerId={customerId}
                  variant='customer'
                  onChanged={() => {
                    fetchInstallmentSummary()
                    // 儲值金扣款收分期會動到餘額
                    fetchCustomerBalance()
                  }}
                />
              </InfoCard>
            )}
          </TabbedAssetWrap>
        </div>
      </ContentGrid>

      {/* 預約記錄（移到頁面最下方，桌機與手機都全寬顯示） */}
      <InfoCard style={{ marginTop: '24px' }}>
        <SectionTitle>
          <span>預約記錄</span>
          {customerBookings?.bookings?.filter((b) => !b.isDeleted).length >
            BOOKING_PREVIEW_COUNT && (
            <Button
              type='link'
              size='small'
              icon={<RightOutlined />}
              onClick={handleViewAllBookings}
              style={{ padding: 0, height: 'auto' }}
            >
              查看全部
            </Button>
          )}
        </SectionTitle>
        {bookingsLoading ? (
          <Spin />
        ) : customerBookings?.bookings?.filter(
            (booking) => !booking.isDeleted
          ).length > 0 ? (
          <div>
            {/* 桌機兩欄各五筆＝一次看十筆；手機自動收成單欄 */}
            <BookingGrid>
              {customerBookings.bookings
                .filter((booking) => !booking.isDeleted)
                .slice(0, BOOKING_PREVIEW_COUNT)
                .map((booking) => {
                  const statusInfo = getStatusDisplay(booking.status)
                  const hasCheckedOut = booking.hasCheckedOut || booking.orderId
                  const amount =
                    booking.discountedPrice || booking.originalPrice || 0
                  /**
                   * 備註優先顯示「結帳備註」。
                   *
                   * 預約備註是客人訂的時候留的（想做什麼、幾點到），
                   * 結帳備註是做完當下打的（今天的狀況、下次要注意什麼）——
                   * 店家回頭翻客戶紀錄時要看的是後者。沒有結帳備註
                   * （還沒結帳，或結帳時沒填）才退回預約備註。
                   */
                  const checkoutNote = String(
                    booking.checkoutRemarks || ''
                  ).trim()
                  const note = checkoutNote || String(booking.note || '').trim()
                  const noteLabel = checkoutNote ? '結帳備註' : '預約備註'

                  return (
                    <BookingRow
                      key={booking._id}
                      onClick={() => handleBookingClick(booking)}
                    >
                      <BookingTopLine>
                        <BookingName
                          title={
                            booking.productName ||
                            booking.services?.[0]?.serviceName ||
                            '服務項目'
                          }
                        >
                          {booking.productName ||
                            booking.services?.[0]?.serviceName ||
                            '服務項目'}
                        </BookingName>
                        <BookingTags>
                          <BookingTag $bg={statusInfo.color}>
                            {statusInfo.text}
                          </BookingTag>
                          {hasCheckedOut && (
                            <BookingTag $bg='#34c759'>已結帳</BookingTag>
                          )}
                        </BookingTags>
                      </BookingTopLine>

                      {/* 日期、時間、設計師擠成一行，金額靠右 —— 原本四段各佔
                          一行，一筆預約就吃掉五行高度 */}
                      <BookingMetaLine>
                        <span className='meta-text'>
                          {dayjs(booking.date).format('YYYY/MM/DD')}{' '}
                          {booking.time}
                          {' · '}
                          {booking.stylistName || '未指定'}
                        </span>
                        <span className='meta-amount'>
                          {formatCurrency(amount)}
                        </span>
                      </BookingMetaLine>

                      {note && (
                        <BookingNote title={`${noteLabel}：${note}`}>
                          <span
                            className={
                              checkoutNote ? 'note-label checkout' : 'note-label'
                            }
                          >
                            {noteLabel}
                          </span>
                          {note}
                        </BookingNote>
                      )}
                    </BookingRow>
                  )
                })}
            </BookingGrid>
            {customerBookings.bookings.filter((booking) => !booking.isDeleted)
              .length > BOOKING_PREVIEW_COUNT && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <Button type='link' onClick={handleViewAllBookings}>
                  查看更多
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Empty description='暫無預約記錄' />
        )}
      </InfoCard>

      {/* 購買同意書（儲值前簽署） */}
      <PurchaseConsentModal
        open={balanceConsent.open}
        onClose={() => setBalanceConsent((c) => ({ ...c, open: false }))}
        businessId={businessId}
        customerId={balanceConsent.customerId}
        customerName={balanceConsent.customerName}
        target='balance'
        variables={balanceConsent.variables}
        onNoForm={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(null, true)
        }}
        onSkip={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(null, true)
        }}
        onSigned={(sigId) => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(sigId, true)
        }}
        onSentRemote={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(null, true)
        }}
      />

      {/* 現代化儲值金操作Modal */}
      <ModernModal
        title={
          (balanceOperation === 'topup' && '💰 儲值') ||
          (balanceOperation === 'deduct' && '💸 扣款') ||
          (balanceOperation === 'refund' && '🔄 退款') ||
          (balanceOperation === 'adjust' && '⚖️ 調整餘額') ||
          '💳 儲值金操作'
        }
        open={balanceModalVisible}
        onCancel={() => setBalanceModalVisible(false)}
        footer={null}
        width={window.innerWidth <= 768 ? '100%' : 420}
        centered
        style={{
          top: window.innerWidth <= 768 ? 0 : undefined,
          paddingBottom: window.innerWidth <= 768 ? 0 : undefined
        }}
        bodyStyle={{
          padding: 0,
          height: window.innerWidth <= 768 ? '100vh' : 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <ModalContent>
          {/* 金額顯示 */}
          <AmountDisplay>
            <div className='amount-label'>
              {balanceOperation === 'adjust' ? '調整金額' : '操作金額'}
            </div>
            <div className='amount-value'>
              {formatAmount(amount)}
              <span className='currency'>NT$</span>
            </div>
          </AmountDisplay>

          {/* 數字鍵盤 */}
          <KeypadContainer>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <KeypadButton
                key={num}
                onClick={() => handleKeypadInput(num.toString())}
              >
                {num}
              </KeypadButton>
            ))}
            <KeypadButton
              className='clear'
              onClick={() => handleKeypadInput('clear')}
            >
              C
            </KeypadButton>
            <KeypadButton onClick={() => handleKeypadInput('0')}>
              0
            </KeypadButton>
            <KeypadButton
              className='backspace'
              onClick={() => handleKeypadInput('backspace')}
            >
              ⌫
            </KeypadButton>
          </KeypadContainer>

          {/* 調整類型按鈕（僅調整操作時顯示） */}
          {balanceOperation === 'adjust' && (
            <>
              <div
                style={{
                  textAlign: 'center',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#64748b',
                  fontWeight: '500'
                }}
              >
                請選擇調整類型 <span style={{ color: '#ef4444' }}>*</span>
              </div>
              <OperationButtons>
                <OperationButton
                  type='increase'
                  selected={adjustmentType === 'increase'}
                  onClick={() => setAdjustmentType('increase')}
                >
                  <PlusCircleOutlined />
                  增加餘額
                </OperationButton>
                <OperationButton
                  type='decrease'
                  selected={adjustmentType === 'decrease'}
                  onClick={() => setAdjustmentType('decrease')}
                >
                  <MinusCircleOutlined />
                  減少餘額
                </OperationButton>
              </OperationButtons>
            </>
          )}

          {/* 備註輸入 */}
          <NoteSection>
            <div className='note-label'>
              {balanceOperation === 'adjust'
                ? '調整原因（必填）'
                : '操作備註（必填）'}
            </div>
            <textarea
              className='note-input'
              rows={3}
              placeholder={
                balanceOperation === 'adjust'
                  ? '請說明調整原因...'
                  : '請輸入操作備註...'
              }
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                maxWidth: '100%'
              }}
            />
          </NoteSection>

          {/* 等級儲值回饋選擇（僅儲值時顯示） */}
          {balanceOperation === 'topup' && (() => {
            const allBonuses = []
            membershipTiers.forEach((tier) => {
              if (tier.isEnabled && tier.topupBonuses) {
                tier.topupBonuses.forEach((bonus) => {
                  allBonuses.push({ ...bonus, tierName: tier.name })
                })
              }
            })
            if (allBonuses.length === 0) return null
            const sortedBonuses = allBonuses.sort((a, b) => Number(a.amount) - Number(b.amount))
            const customerTier = customer?.businessSpecificData?.membershipLevel || ''
            return (
              <div style={{ paddingBottom: 8 }}>
                <div style={{ marginBottom: 6, fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  等級儲值回饋（選填）
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {sortedBonuses.map((bonus, index) => {
                    const amt = Number(bonus.amount) || 0
                    const bonusAmt = Number(bonus.bonus) || 0
                    const isSelected =
                      !balanceBonusOptedOut &&
                      balanceSelectedTopupBonus &&
                      balanceSelectedTopupBonus.amount === amt &&
                      balanceSelectedTopupBonus.tierName === bonus.tierName
                    return (
                      <button
                        key={index}
                        type='button'
                        onClick={() => {
                          setBalanceSelectedTopupBonus(bonus)
                          setBalanceBonusOptedOut(false)
                          setAmount(String(amt))
                        }}
                        style={{
                          borderRadius: 8,
                          height: 44,
                          padding: '0 12px',
                          fontSize: 13,
                          border: isSelected ? '2px solid #d5728a' : '1px solid #d9d9d9',
                          background: isSelected ? '#d5728a' : '#fff',
                          color: isSelected ? '#fff' : '#333',
                          cursor: 'pointer',
                          lineHeight: '1.2',
                          textAlign: 'left'
                        }}
                      >
                        <div>
                          <span style={{ fontWeight: 600 }}>
                            儲 {amt.toLocaleString()} 送 {bonusAmt.toLocaleString()}
                          </span>
                          <br />
                          <span style={{ fontSize: 10, opacity: 0.7 }}>
                            (等級：{bonus.tierName})
                          </span>
                        </div>
                      </button>
                    )
                  })}
                  <button
                    type='button'
                    onClick={() => {
                      setBalanceBonusOptedOut(true)
                      setBalanceSelectedTopupBonus(null)
                    }}
                    style={{
                      borderRadius: 8,
                      height: 44,
                      padding: '0 12px',
                      fontSize: 13,
                      border: balanceBonusOptedOut ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
                      background: balanceBonusOptedOut ? '#fff1f0' : '#fff',
                      color: balanceBonusOptedOut ? '#ff4d4f' : '#333',
                      cursor: 'pointer'
                    }}
                  >
                    不套用活動方案
                  </button>
                </div>
              </div>
            )
          })()}

          {/* 儲值活動、使用期限、銷售人員（僅儲值時顯示，與鍵盤/備註同寬） */}
          {balanceOperation === 'topup' && (
            <div
              style={{
                paddingBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12
              }}
            >
              <div>
                <div
                  style={{
                    marginBottom: 4,
                    fontSize: 13,
                    color: '#64748b',
                    fontWeight: 500
                  }}
                >
                  銷售人員（選填，有選才會列入儲值紀錄／業績／抽成）
                </div>
                <Select
                  placeholder='選擇銷售人員'
                  allowClear
                  style={{ width: '100%' }}
                  value={balanceSalesPersonnelId || undefined}
                  onChange={(v) => setBalanceSalesPersonnelId(v || null)}
                  options={employees.map((e) => ({
                    value: e._id,
                    label: e.name
                  }))}
                />
              </div>
              <div>
                <div
                  style={{
                    marginBottom: 4,
                    fontSize: 13,
                    color: '#64748b',
                    fontWeight: 500
                  }}
                >
                  儲值活動（選填）
                </div>
                <TopupSchemeKeyInput
                  businessId={businessId}
                  value={balanceSchemeKey}
                  onChange={setBalanceSchemeKey}
                  placeholder='可輸入或從下拉選擇曾用過的儲值活動'
                  extraSchemeKeys={
                    balanceInfo?.balanceByScheme
                      ?.map((s) => s.schemeKey)
                      .filter(Boolean) ?? []
                  }
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <div
                  style={{
                    marginBottom: 4,
                    fontSize: 13,
                    color: '#64748b',
                    fontWeight: 500
                  }}
                >
                  使用期限（選填）
                </div>
                <DatePicker
                  value={balanceExpiryDate}
                  onChange={(d) => setBalanceExpiryDate(d || null)}
                  format='YYYY-MM-DD'
                  style={{ width: '100%' }}
                  placeholder='選擇使用期限'
                />
              </div>

              {/* 指派會員等級（選填） */}
              {membershipTiers.length > 0 && (
                <div>
                  <div
                    style={{
                      marginBottom: 4,
                      fontSize: 13,
                      color: '#64748b',
                      fontWeight: 500
                    }}
                  >
                    指派會員等級（選填）
                  </div>
                  <Select
                    allowClear
                    value={balanceAssignTierName || undefined}
                    onChange={(v) => setBalanceAssignTierName(v || null)}
                    placeholder='不變更等級'
                    style={{ width: '100%' }}
                    options={membershipTiers
                      .filter((t) => t.isEnabled)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((t) => ({
                        value: t.name,
                        label: t.name + (t.discountRate && t.discountRate < 100 ? `（${t.discountRate}折）` : '')
                      }))}
                  />
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    選擇後將直接指派等級，搭配使用期限可同時設定會籍到期日
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 警告提示（僅調整操作時顯示） */}
          {balanceOperation === 'adjust' && (
            <WarningBox>
              <div className='warning-text'>
                ⚠️ 手動調整功能請謹慎使用，建議僅在系統錯誤或特殊情況下使用
              </div>
            </WarningBox>
          )}

          {/* 操作按鈕 */}
          <ActionButtons>
            <button
              className='cancel-btn'
              onClick={() => {
                setBalanceSchemeKey('')
                setBalanceExpiryDate(null)
                setBalanceSalesPersonnelId(null)
                setBalanceSelectedTopupBonus(null)
                setBalanceBonusOptedOut(false)
                setBalanceAssignTierName(null)
                setBalanceModalVisible(false)
              }}
            >
              取消
            </button>
            <button
              className='confirm-btn'
              onClick={handleBalanceSubmit}
              disabled={
                balanceSubmitting ||
                !amount ||
                parseInt(amount) <= 0 ||
                (balanceOperation === 'adjust' && !adjustmentType) ||
                (balanceOperation === 'adjust' && !note.trim())
              }
              style={{
                opacity:
                  balanceSubmitting ||
                  !amount ||
                  parseInt(amount) <= 0 ||
                  (balanceOperation === 'adjust' && !adjustmentType) ||
                  (balanceOperation === 'adjust' && !note.trim())
                    ? 0.5
                    : 1,
                cursor:
                  balanceSubmitting ||
                  !amount ||
                  parseInt(amount) <= 0 ||
                  (balanceOperation === 'adjust' && !adjustmentType) ||
                  (balanceOperation === 'adjust' && !note.trim())
                    ? 'not-allowed'
                    : 'pointer'
              }}
            >
              {balanceSubmitting && '處理中…'}
              {!balanceSubmitting && balanceOperation === 'topup' && '確認儲值'}
              {!balanceSubmitting && balanceOperation === 'deduct' && '確認扣款'}
              {!balanceSubmitting && balanceOperation === 'refund' && '確認退款'}
              {!balanceSubmitting && balanceOperation === 'adjust' && '確認調整'}
            </button>
          </ActionButtons>
        </ModalContent>
      </ModernModal>

      {/* 結帳信息 Modal */}
      <Modal
        title='結帳信息'
        open={checkoutModalVisible}
        onCancel={() => {
          setCheckoutModalVisible(false)
          setCheckoutOrderData(null)
        }}
        footer={null}
        width={window.innerWidth <= 768 ? '95%' : 800}
        style={{ top: 20 }}
      >
        <Spin spinning={loadingCheckout}>
          {checkoutOrderData && (
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 16
              }}
            >
              {/* 訂單標題 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                  paddingBottom: 16,
                  borderBottom: '1px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#0f2562'
                  }}
                >
                  {checkoutOrderData.orderId ||
                    checkoutOrderData._id ||
                    '未命名訂單'}
                </h3>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 6
                  }}
                >
                  <span
                    style={{
                      padding: '2px 12px',
                      borderRadius: '999px',
                      fontSize: 12,
                      fontWeight: 500,
                      background:
                        checkoutOrderData.status === 'paid'
                          ? '#52c41a'
                          : checkoutOrderData.status === 'voided'
                            ? '#ff4d4f'
                            : '#1890ff',
                      color: '#fff'
                    }}
                  >
                    {checkoutOrderData.status === 'paid'
                      ? '已付款'
                      : checkoutOrderData.status === 'voided'
                        ? '已作廢'
                        : '已付款'}
                  </span>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    <CalendarOutlined />{' '}
                    {checkoutOrderData.createdAt || checkoutOrderData.time?.date
                      ? dayjs(
                          checkoutOrderData.createdAt ||
                            checkoutOrderData.time.date
                        ).format('YYYY-MM-DD HH:mm')
                      : '--'}
                  </span>
                </div>
              </div>

              {/* 客戶資訊 */}
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0f2562',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 12
                  }}
                >
                  <UserOutlined /> 客戶資訊
                </div>
                <div
                  style={{
                    display: 'grid',
                    gap: 6,
                    fontSize: 14,
                    color: '#576079'
                  }}
                >
                  <span>
                    姓名：
                    <strong>{checkoutOrderData.customer?.name || '--'}</strong>
                  </span>
                  <span>
                    電話：
                    <strong>{checkoutOrderData.customer?.phone || '--'}</strong>
                  </span>
                  <span>
                    消費日期：<strong>{checkoutOrderData.time?.date}</strong>
                  </span>
                </div>
              </div>

              {/* 服務明細 */}
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0f2562',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 12
                  }}
                >
                  <CalendarOutlined /> 服務明細
                </div>
                {renderServices(
                  checkoutOrderData.services || checkoutOrderData.items || []
                )}
              </div>

              {/* 商品明細 */}
              {checkoutOrderData.products &&
                checkoutOrderData.products.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#0f2562',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 12
                      }}
                    >
                      <ShoppingOutlined /> 商品明細
                    </div>
                    {renderProducts(checkoutOrderData.products)}
                  </div>
                )}

              {/* 施作照片：照片掛在預約上，純賣商品（沒關聯預約）的訂單就沒有。
                  元件自己判斷方案與權限，沒開通會 return null。 */}
              {(checkoutOrderData.bookingId ||
                checkoutOrderData.booking?._id) && (
                <ServicePhotoThumbs
                  businessId={businessId}
                  bookingId={String(
                    checkoutOrderData.bookingId ||
                      checkoutOrderData.booking?._id
                  )}
                />
              )}

              {/* 金額資訊 */}
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0f2562',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 12
                  }}
                >
                  <DollarOutlined /> 金額資訊
                </div>
                <div
                  style={{
                    display: 'grid',
                    gap: 6,
                    fontSize: 14,
                    color: '#576079'
                  }}
                >
                  {checkoutOrderData.amounts?.servicesAmount > 0 && (
                    <span>
                      服務小計：
                      <strong style={{ color: '#0f2562' }}>
                        {formatCurrency(
                          checkoutOrderData.amounts.servicesAmount || 0
                        )}
                      </strong>
                    </span>
                  )}
                  {checkoutOrderData.amounts?.productsAmount > 0 && (
                    <span>
                      商品小計：
                      <strong style={{ color: '#0f2562' }}>
                        {formatCurrency(
                          checkoutOrderData.amounts.productsAmount || 0
                        )}
                      </strong>
                    </span>
                  )}
                  <span>
                    應收金額：
                    <strong style={{ color: '#0f2562' }}>
                      {formatCurrency(
                        checkoutOrderData.amounts?.receivable || 0
                      )}
                    </strong>
                  </span>
                  <span>
                    已付金額：
                    <strong style={{ color: '#0f2562' }}>
                      {formatCurrency(checkoutOrderData.amounts?.paid || 0)}
                    </strong>
                  </span>
                  {checkoutOrderData.amounts?.unpaid !== undefined && (
                    <span>
                      未付金額：
                      <strong style={{ color: '#0f2562' }}>
                        {formatCurrency(checkoutOrderData.amounts.unpaid)}
                      </strong>
                    </span>
                  )}
                </div>
              </div>

              {renderCouponDeduction(checkoutOrderData) && (
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0f2562',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 12
                    }}
                  >
                    <Tag color='magenta' style={{ marginRight: 0 }}>
                      優惠券
                    </Tag>
                    優惠券折抵
                  </div>
                  {renderCouponDeduction(checkoutOrderData)}
                </div>
              )}

              {/* 付款方式 */}
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0f2562',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 12
                  }}
                >
                  <FilterOutlined /> 付款方式
                </div>
                {renderPaymentBreakdown(checkoutOrderData.payment || {})}
              </div>

              {/* 備註：結帳當下打的字（客人的狀況、下次要注意的事），
                  沒填就整塊不顯示，不要留一個空欄位 */}
              {String(checkoutOrderData.remarks || '').trim() && (
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0f2562',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 12
                    }}
                  >
                    <FileTextOutlined /> 備註
                  </div>
                  <div
                    style={{
                      background: '#fafafa',
                      border: '1px solid #eee',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 13,
                      lineHeight: 1.7,
                      color: '#444',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {checkoutOrderData.remarks}
                  </div>
                </div>
              )}
            </div>
          )}
        </Spin>
      </Modal>

      {/* 預約記錄 Drawer */}
      <BookingDrawer
        title={`${displayData.name}的預約記錄`}
        placement='right'
        width='100%'
        onClose={() => {
          setBookingsDrawerVisible(false)
        }}
        open={bookingsDrawerVisible}
      >
        <BookingTable
          isCustomerView={true}
          customerInfo={customer}
          onPageChange={(page) => fetchAllCustomerBookings(page)}
          bookings={allCustomerBookings}
          loading={loadingAllBookings}
        />
      </BookingDrawer>

      {/* 會員等級調整 Modal */}
      <Modal
        open={tierAdjustOpen}
        title='調整會員等級'
        okText='儲存'
        cancelText='取消'
        confirmLoading={tierAdjustSubmitting}
        onOk={submitTierAdjust}
        onCancel={() => setTierAdjustOpen(false)}
        destroyOnClose
        width={520}
      >
        <Form form={tierAdjustForm} layout='vertical'>
          <Form.Item
            label='會員級別'
            name='membershipLevel'
            rules={[{ required: true, message: '請選擇會員等級' }]}
          >
            <Select
              placeholder='請選擇等級'
              options={membershipTiers.map((t) => ({
                value: t.name,
                label: `${t.name}（${t.discountRate}${t.discountRate === 100 ? '% 無折扣' : '%'}）`
              }))}
            />
          </Form.Item>

          <Form.Item label='會員到期日'>
            <Space direction='vertical' style={{ width: '100%' }}>
              <Form.Item
                name='tierExpiresAt'
                noStyle
                rules={[
                  {
                    validator: (_, v) => {
                      if (tierAdjustPermanent) return Promise.resolve()
                      if (!v) return Promise.reject(new Error('請選擇日期或勾選永久'))
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  showTime={{ format: 'HH:mm' }}
                  format='YYYY-MM-DD HH:mm'
                  disabled={tierAdjustPermanent}
                  placeholder='選擇到期日'
                />
              </Form.Item>
              <Checkbox
                checked={tierAdjustPermanent}
                onChange={(e) => {
                  setTierAdjustPermanent(e.target.checked)
                  if (e.target.checked) {
                    tierAdjustForm.setFieldsValue({ tierExpiresAt: null })
                  }
                }}
              >
                永久不到期
              </Checkbox>
            </Space>
          </Form.Item>

          <Form.Item
            label='消費認列起始日（選填）'
            name='tierAccumulationStartDate'
            tooltip='累計消費從這天起算；不填代表沿用原本的起算日'
          >
            <DatePicker
              style={{ width: '100%' }}
              format='YYYY-MM-DD'
              placeholder='選填'
            />
          </Form.Item>

          <Form.Item name='resetAccumulated' valuePropName='checked'>
            <Checkbox>
              同時將累計實付金額歸零
              <Tooltip
                placement='right'
                title={
                  <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                    <div style={{ marginBottom: 6 }}>
                      <b>什麼是累計實付金額？</b>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      會員從目前等級被授予以來，在本店實際付出的金額總和（不含
                      儲值金抵扣，已扣優惠券）。
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <b>用來幹嘛？</b>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      作為自動升等／續等／降等的判斷依據；客戶端的「距下一級還差 NT$
                      XXX」進度條也是用它計算。
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <b>勾選歸零的情境：</b>
                    </div>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>客戶被降等想徹底重置</li>
                      <li>升等後想從新等級重新累計</li>
                      <li>匯入錯誤要修正</li>
                    </ul>
                    <div style={{ marginTop: 6, color: '#fff7e0' }}>
                      不勾 = 保留原數字。若該數字達下一級門檻，下次結帳系統會立刻
                      又升回去。
                    </div>
                  </div>
                }
                overlayStyle={{ maxWidth: 320 }}
              >
                <QuestionCircleOutlined
                  style={{
                    marginLeft: 6,
                    color: '#999',
                    cursor: 'help'
                  }}
                />
              </Tooltip>
            </Checkbox>
          </Form.Item>

          <Form.Item
            label={
              <span>
                店家備註{' '}
                <span style={{ color: '#888', fontSize: 12, fontWeight: 400 }}>
                  （會記錄到變動歷程）
                </span>
              </span>
            }
            name='note'
          >
            <Input.TextArea
              rows={3}
              maxLength={300}
              placeholder='例如：客戶申請降級／VIP 升等專案／補件到期日…'
            />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  )
}

// 預約記錄 Drawer 樣式組件
const BookingDrawer = styled(Drawer)`
  &.ant-drawer {
    z-index: 1050;

    .ant-drawer-content-wrapper {
      @media (max-width: 820px) {
        width: 100% !important;
      }
    }

    .ant-drawer-body {
      height: calc(100% - 55px);
      overflow: auto;
      padding: 24px;

      @media (max-width: 820px) {
        padding: 16px;
      }
    }

    .ant-drawer-content {
      height: 100%;
      overflow: hidden;
    }

    .ant-drawer-header {
      position: sticky;
      top: 0;
      z-index: 1;
      background: ${CD_SURFACE};
      border-bottom: 1px solid ${CD_BORDER};
    }
  }
`

export default CustomerDetail
